import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import {
    COLORS,
    cardSx,
    FinanceCard,
    FinanceSectionHeader,
    FinanceKpiTile,
    FinanceHeroStat,
    SPACE,
    FinanceDetailsSection,
} from "../shared/FinanceUiKit";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { Avatar, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, InputAdornment, LinearProgress, MenuItem, Skeleton, Tooltip } from "@mui/material";
// Büyük formda her tuşta tüm formun yeniden çizilmesini önleyen tamponlu TextField (yazma gecikmesi)
import TextField from "components/BufferedTextField";

import {
    AccountBalanceWallet,
    Add,
    ArrowDownward,
    ArrowUpward,
    CheckCircle,
    Download,
    DirectionsBoat,
    FilterAlt,
    Flight,
    Paid,
    LocalShipping,
    Analytics,
    ContentCopy,
    Inventory2,
    PictureAsPdf,
    ReceiptLong,
    Refresh,
    RestartAlt,
    Search,
    UploadFile,
    Visibility,
    WarningAmber,
} from "@mui/icons-material";

import FinanceStatusChip from "../components/FinanceStatusChip";
import { formatDate } from "../utils/formatter";

import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";
import { appAlert } from "components/AppDialog";
// Koyu hero bandı için (yedekten geri yüklendi)
import { WorkspacePremium } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

const getValue = (row, camel, snake, fallback = "") =>
    row?.[camel] ?? row?.[snake] ?? fallback;

const toNumber = (value) => {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefinedNumber = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && item !== "",
    );

    return toNumber(value);
};

const getEntityId = (value) => {
    if (!value) return "";

    if (typeof value === "object") {
        return getEntityId(value?._id || value?.id || value?.$oid);
    }

    return String(value).trim();
};

const getDocumentType = (row) => {
    const explicitType = String(row?.documentType || row?.document_type || "")
        .trim()
        .toLowerCase();
    const hasReturnIdentity = Boolean(
        row?.returnInvoiceId ||
        row?.return_invoice_id ||
        row?.returnType ||
        row?.return_type ||
        ((row?.returnStatus || row?.return_status) &&
            (row?.originalInvoiceId || row?.original_invoice_id)),
    );

    return explicitType === "return_invoice" || hasReturnIdentity
        ? "return_invoice"
        : "invoice";
};

const isReturnInvoice = (row) => getDocumentType(row) === "return_invoice";

const normalizeCurrencyCode = (currency) => {
    const code = String(currency || "EUR")
        .trim()
        .toUpperCase();

    if (code === "EURO") return "EUR";
    if (code === "TL") return "TRY";

    return code || "EUR";
};

const getCurrency = (row) =>
    normalizeCurrencyCode(
        row?.costCurrency ||
        row?.cost_currency ||
        row?.currency ||
        row?.invoiceCurrency ||
        row?.invoice_currency ||
        row?.totalCostCurrency ||
        row?.total_cost_currency ||
        row?.financialSummary?.costCurrency ||
        row?.financialSummary?.totalCurrency ||
        row?.financial_summary?.total_currency ||
        row?.freightCharges?.totalCurrency ||
        row?.freight_charges?.total_currency ||
        "EUR",
    );

const getTotalAmount = (row) =>
    firstDefinedNumber(
        row?.totalAmount,
        row?.total_amount,
        row?.invoiceAmount,
        row?.invoice_amount,
    );

const getVatAmount = (row) =>
    firstDefinedNumber(row?.vatAmount, row?.vat_amount);

const getNetPayable = (row) =>
    firstDefinedNumber(
        row?.netPayableAmount,
        row?.net_payable_amount,
        getTotalAmount(row),
    );

const getBuyingRate = (row) => {
    if (isReturnInvoice(row)) return 0;

    return firstDefinedNumber(
        row?.buyingRate,
        row?.buying_rate,

        row?.financialSummary?.buyingRate,
        row?.financial_summary?.buying_rate,

        row?.freightCharges?.buyingRate,
        row?.freight_charges?.buying_rate,
    );
};

const getOperationalCost = (row) => {
    const summary = row?.financialSummary || row?.financial_summary || {};

    const freightCharges = row?.freightCharges || row?.freight_charges || {};

    const explicitOperationalCost = firstDefinedNumber(
        row?.operationalCost,
        row?.operational_cost,

        summary?.operationalCost,
        summary?.operational_cost,
    );

    if (explicitOperationalCost > 0) {
        return explicitOperationalCost;
    }

    const roadCost =
        firstDefinedNumber(summary?.shipmentCostTotal) +
        firstDefinedNumber(summary?.convertedLinehaulCost) +
        firstDefinedNumber(summary?.convertedHandlingCost);

    const oceanCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.oceanFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const airCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.airFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const freightChargeCost =
        firstDefinedNumber(freightCharges?.pickupTotal) +
        firstDefinedNumber(freightCharges?.othc?.total) +
        firstDefinedNumber(freightCharges?.oceanFreight?.total) +
        firstDefinedNumber(freightCharges?.airFreight?.total) +
        firstDefinedNumber(freightCharges?.dthc?.total) +
        firstDefinedNumber(freightCharges?.handling?.total) +
        firstDefinedNumber(freightCharges?.deliveryTotal);

    if (roadCost > 0) {
        return roadCost;
    }

    if (oceanCost > 0) {
        return oceanCost;
    }

    if (airCost > 0) {
        return airCost;
    }

    if (freightChargeCost > 0) {
        return freightChargeCost;
    }

    return 0;
};

const getMasterTotalCost = (row) => {
    if (isReturnInvoice(row)) return 0;

    const explicitTotalCost = firstDefinedNumber(
        row?.actualCostTotal,
        row?.actual_cost_total,

        row?.totalCost,
        row?.total_cost,

        row?.financialSummary?.totalCost,
        row?.financial_summary?.total_cost,
    );

    if (explicitTotalCost > 0) {
        // Backend tarafindan hesaplanmis toplam maliyet nihai degerdir.
        // Buying rate'i tekrar eklemek ayni maliyeti iki kez sayar.
        return explicitTotalCost;
    }

    return getOperationalCost(row) + getBuyingRate(row);
};

const getFinanceJobKey = (row, index = 0) =>
    getEntityId(
        row?.financeJobId ||
        row?.finance_job_id ||
        row?.financeJob?._id ||
        row?.finance_job?._id ||
        row?.shipmentId ||
        row?.shipment_id,
    ) ||
    String(
        row?.jobNo || row?.job_no || row?.shipmentNo || row?.shipment_no || "",
    ).trim() ||
    `unlinked-${getEntityId(row?._id || row?.id) || index}`;

const getUniqueFinanceJobs = (rows) => {
    const jobs = new Map();

    rows.forEach((row, index) => {
        const key = getFinanceJobKey(row, index);
        const current = jobs.get(key);

        // Ayni Finance Job birden fazla faturada tekrar edebilir. Master cost
        // icin en dolu/yuksek kaydi temsilci olarak tutuyoruz.
        if (!current || getMasterTotalCost(row) > getMasterTotalCost(current)) {
            jobs.set(key, row);
        }
    });

    return Array.from(jobs.values());
};

const isIncludedInMasterCost = (row) =>
    row?.includedInMasterCost === true || row?.included_in_master_cost === true;

const getSupplierInvoiceCost = (row) => {
    if (isIncludedInMasterCost(row)) {
        return 0;
    }

    return getNetPayable(row);
};

const getShipmentNo = (row) =>
    getValue(row, "jobNo", "job_no") ||
    row?.shipmentNo ||
    row?.shipment_no ||
    row?.invoiceNo ||
    row?.invoice_no ||
    "Multiple Jobs";

const getSupplierName = (row) =>
    getValue(row, "supplierName", "supplier_name", "N/A");

const getCustomerName = (row) =>
    getValue(row, "customerName", "customer_name", row?.customer || "N/A");

const getDateValue = (row) => {
    const raw =
        row?.returnDate ||
        row?.return_date ||
        row?.issuedAt ||
        row?.issued_at ||
        row?.invoiceDate ||
        row?.invoice_date ||
        row?.supplierInvoiceDate ||
        row?.supplier_invoice_date ||
        row?.createdAt ||
        row?.created_at ||
        row?.updatedAt ||
        row?.updated_at ||
        row?.dueDate ||
        row?.due_date;

    const date = raw ? new Date(raw) : null;
    return date && !Number.isNaN(date.getTime()) ? date : null;
};

const getReturnStatus = (row) =>
    String(row?.returnStatus || row?.return_status || "Draft").trim();

const getOriginalInvoiceId = (row) =>
    getEntityId(
        row?.originalInvoiceId ||
        row?.original_invoice_id ||
        row?.originalInvoice?._id ||
        row?.originalInvoice?.id,
    );

const getOriginalInvoiceNo = (row) =>
    row?.originalInvoiceNumber ||
    row?.original_invoice_number ||
    row?.originalInvoiceNo ||
    row?.original_invoice_no ||
    row?.originalInvoice?.invoiceNo ||
    row?.originalInvoice?.invoice_no ||
    "";

const getLinkedReturnInvoices = (row) => {
    const candidates = [
        row?.returnInvoices,
        row?.return_invoices,
        row?.linkedReturnInvoices,
        row?.linked_return_invoices,
    ];

    return candidates.find(Array.isArray) || [];
};

const getRawReturnAmount = (row) => {
    const explicitAmount = Math.abs(
        firstDefinedNumber(
            row?.returnedAmount,
            row?.returned_amount,
            row?.actualInvoiceAmount,
            row?.actual_invoice_amount,
            row?.netPayableAmount,
            row?.net_payable_amount,
            row?.totalWithVat,
            row?.total_with_vat,
            row?.returnedTotalWithVat,
            row?.returned_total_with_vat,
            row?.totalAmount,
            row?.total_amount,
            row?.invoiceSalesAmount,
            row?.amount,
        ),
    );

    if (explicitAmount > 0) return explicitAmount;

    const items =
        [row?.invoiceItems, row?.invoice_items, row?.items].find(Array.isArray) ||
        [];

    return items.reduce(
        (sum, item) =>
            sum +
            Math.abs(
                firstDefinedNumber(
                    item?.totalWithVat,
                    item?.total_with_vat,
                    item?.amount,
                    item?.lineAmount,
                    item?.line_amount,
                ),
            ),
        0,
    );
};

const getReturnSummary = (row) => {
    if (isReturnInvoice(row)) {
        const issued = getReturnStatus(row).toLowerCase() === "issued";

        return {
            status: getReturnStatus(row),
            issuedAmount: issued ? getRawReturnAmount(row) : 0,
            activeAmount:
                getReturnStatus(row).toLowerCase() === "cancelled"
                    ? 0
                    : getRawReturnAmount(row),
            count: 1,
            latestReturnInvoice: row,
        };
    }

    const allReturns = getLinkedReturnInvoices(row);
    const activeReturns = allReturns.filter(
        (item) => getReturnStatus(item).toLowerCase() !== "cancelled",
    );
    const issuedReturns = activeReturns.filter(
        (item) => getReturnStatus(item).toLowerCase() === "issued",
    );
    const sortByDateDesc = (a, b) =>
        (getDateValue(b)?.getTime() || 0) - (getDateValue(a)?.getTime() || 0);
    const latestActiveReturn = [...activeReturns].sort(sortByDateDesc)[0] || null;
    const latestReturnInvoice = [...allReturns].sort(sortByDateDesc)[0] || null;
    const issuedAmount = issuedReturns.reduce(
        (sum, item) => sum + getRawReturnAmount(item),
        0,
    );
    const activeAmount = activeReturns.reduce(
        (sum, item) => sum + getRawReturnAmount(item),
        0,
    );
    const originalAmount = Math.abs(getNetPayable(row));
    const latestStatus = latestActiveReturn
        ? getReturnStatus(latestActiveReturn).toLowerCase()
        : "";

    let status = "No Return";
    if (latestStatus === "draft") status = "Return Draft";
    else if (latestStatus === "approved") status = "Return Approved";
    else if (
        issuedAmount > 0 &&
        originalAmount > 0 &&
        issuedAmount >= originalAmount - 0.01
    ) {
        status = "Full Return";
    } else if (issuedAmount > 0) status = "Partial Return";
    else if (activeAmount > 0) status = "Return Pending";
    else if (allReturns.length > 0) status = "Return Cancelled";

    return {
        status,
        issuedAmount,
        activeAmount,
        count: allReturns.length,
        latestReturnInvoice,
    };
};

const getNetInvoicePayable = (row) => {
    if (isReturnInvoice(row)) {
        return getReturnStatus(row).toLowerCase() === "issued"
            ? -getRawReturnAmount(row)
            : 0;
    }

    return Math.max(getNetPayable(row) - getReturnSummary(row).issuedAmount, 0);
};

const getNetInvoiceTotal = (row) => {
    if (isReturnInvoice(row)) return getNetInvoicePayable(row);

    return Math.max(getTotalAmount(row) - getReturnSummary(row).issuedAmount, 0);
};

const getNetSupplierInvoiceCost = (row) => {
    if (isReturnInvoice(row)) return getNetInvoicePayable(row);
    if (isIncludedInMasterCost(row)) return 0;

    return getNetInvoicePayable(row);
};

const RETURN_STATUS_STYLES = {
    "No Return": { color: "#475467", background: "#F2F4F7", border: "#D0D5DD" },
    Draft: { color: "#B54708", background: "#FFFAEB", border: "#FEDF89" },
    "Return Draft": {
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    Approved: { color: "#175CD3", background: "#EFF8FF", border: "#B2DDFF" },
    "Return Approved": {
        color: "#175CD3",
        background: "#EFF8FF",
        border: "#B2DDFF",
    },
    Issued: { color: "#027A48", background: "#ECFDF3", border: "#ABEFC6" },
    "Partial Return": {
        color: "#027A48",
        background: "#ECFDF3",
        border: "#ABEFC6",
    },
    "Full Return": { color: "#6941C6", background: "#F4F3FF", border: "#D9D6FE" },
    "Return Pending": {
        color: "#175CD3",
        background: "#EFF8FF",
        border: "#B2DDFF",
    },
    Cancelled: { color: "#B42318", background: "#FEF3F2", border: "#FECDCA" },
    "Return Cancelled": {
        color: "#B42318",
        background: "#FEF3F2",
        border: "#FECDCA",
    },
};

const normalizeCountry = (value) =>
    String(value || "")
        .trim()
        .toLocaleUpperCase("tr-TR")
        .replace(/İ/g, "I")
        .replace(/Ü/g, "U");
const normalizeLookupKey = (value) => {
    if (!value) return "";
    if (typeof value === "object")
        return normalizeLookupKey(
            value?._id ||
            value?.id ||
            value?.$oid ||
            value?.nameKey ||
            value?.name ||
            value?.companyName,
        );
    return String(value)
        .trim()
        .toLocaleUpperCase("tr-TR")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Z0-9]+/g, " ")
        .trim();
};
const getDirectCountries = (item) =>
    [
        item,
        item?.customer,
        item?.serviceProvider,
        item?.supplier,
        item?.globalAgent,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
    ]
        .filter((value) => value && typeof value === "object")
        .flatMap((value) => [
            value?.customerCountry,
            value?.serviceProviderCountry,
            value?.globalAgentCountry,
            ...(Array.isArray(value?.servicesCountry) ? value.servicesCountry : []),
        ])
        .filter(Boolean);
const getPartyKeys = (item) =>
    [
        item?.customerId,
        item?.customer,
        item?.customerName,
        item?.customer_name,
        item?.serviceProviderId,
        item?.serviceProvider,
        item?.serviceProviderName,
        item?.supplierId,
        item?.supplier,
        item?.supplierName,
        item?.supplier_name,
        item?.globalAgentId,
        item?.globalAgent,
        item?.globalAgentName,
        item?.selectedBookingAgent,
        item?.selectedOriginAgent,
        item?.selectedDestinationAgent,
        item?.selectedNotifyAgent,
    ]
        .map(normalizeLookupKey)
        .filter(Boolean);
const getGeographicScope = (item, lookup = new Map()) => {
    const countries = [
        ...getDirectCountries(item),
        ...getPartyKeys(item).flatMap((key) => lookup.get(key) || []),
    ];
    return countries.some((country) =>
        ["TURKEY", "TURKIYE"].includes(normalizeCountry(country)),
    )
        ? "DOMESTIC"
        : "INTERNATIONAL";
};
const getGeographicLabel = (item, lookup) =>
    getGeographicScope(item, lookup) === "DOMESTIC"
        ? "Domestic"
        : "International";

const getInvoiceNo = (row) =>
    (isReturnInvoice(row)
        ? row?.returnInvoiceNumber ||
        row?.return_invoice_number ||
        row?.returnInvoiceNo ||
        row?.return_invoice_no
        : "") || getValue(row, "invoiceNo", "invoice_no", "N/A");

const getApprovalStatus = (row) =>
    getValue(row, "approvalStatus", "approval_status", "Pending");

const getPaymentStatus = (row) =>
    getValue(row, "paymentStatus", "payment_status", "Not Due");

const getDuplicateStatus = (row) =>
    getValue(row, "duplicateCheckStatus", "duplicate_check_status", "Clean");

const getOverdueDays = (dueDate, paymentStatus) => {
    if (!dueDate) {
        return 0;
    }

    const status = String(paymentStatus || "").toLowerCase();

    // Exact match only: "unpaid" and "partially paid" both contain the
    // substring "paid" and must still be treated as (potentially) overdue.
    if (status === "paid") {
        return 0;
    }

    const today = new Date();
    const due = new Date(dueDate);

    if (Number.isNaN(due.getTime())) {
        return 0;
    }

    // Whole calendar days past the due date: an invoice due today is not
    // overdue yet, and the time of day must not round a partial day up.
    const startOfDay = (value) =>
        new Date(value.getFullYear(), value.getMonth(), value.getDate());
    const days = Math.round(
        (startOfDay(today).getTime() - startOfDay(due).getTime()) / 86400000,
    );

    return days > 0 ? days : 0;
};

const formatCompact = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(toNumber(value));

const formatNumber = (value) =>
    new Intl.NumberFormat("en-US", {
        maximumFractionDigits: 0,
    }).format(toNumber(value));

// Options for the display-currency selector (which currency the aggregated
// KPIs / charts / CSV totals are shown in). NOTE: this is a different concept
// from `currencyFilter`, which decides WHICH RECORDS are listed based on each
// supplier invoice's own currency.
const CURRENCY_OPTIONS = ["EUR", "USD", "TRY", "GBP"];

const normalizeText = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase()
        .replace(/[ıİ]/g, "i");

const getInvoiceOrigin = (item) =>
    normalizeText(item?.invoiceOrigin || item?.invoice_origin || "tms") ===
    "manual"
        ? "manual"
        : "tms";

const getTransportMode = (item) => {
    if (getInvoiceOrigin(item) === "manual") {
        return "manual";
    }

    const candidates = [
        item?.sourceType,
        item?.source_type,
        item?.transportMode,
        item?.transport_mode,
        item?.shipmentMode,
        item?.shipment_mode,
        item?.productType,
        item?.product_type,
        item?.serviceType,
        item?.service_type,
        item?.tmsSource,
        item?.tms_source,
        item?.module,
        item?.source,
        getShipmentNo(item),
    ]
        .map(normalizeText)
        .join(" ");

    if (
        candidates.includes("ocean") ||
        candidates.includes("sea") ||
        candidates.includes("fcl") ||
        candidates.includes("lcl") ||
        candidates.includes("ohfl") ||
        candidates.includes("ombl")
    )
        return "ocean";

    if (
        candidates.includes("air") ||
        candidates.includes("flight") ||
        candidates.includes("ahfl") ||
        candidates.includes("ambl")
    )
        return "air";

    return "road";
};

const getShipmentType = (item) => {
    const raw =
        item?.shipmentType ||
        item?.shipment_type ||
        item?.transportType ||
        item?.transport_type ||
        item?.oceanTransportType ||
        item?.ocean_transport_type ||
        item?.airTransportType ||
        item?.air_transport_type ||
        item?.serviceType ||
        item?.service_type ||
        "General";

    return String(raw).trim() || "General";
};

const MODE_CONFIG = {
    ocean: {
        label: "Ocean",
        color: "#1570EF",
        light: "#EFF8FF",
        border: "#B2DDFF",
        icon: DirectionsBoat,
    },
    road: {
        label: "Road",
        color: "#B54708",
        light: "#FFFAEB",
        border: "#FEDF89",
        icon: LocalShipping,
    },
    air: {
        label: "Air",
        color: "#6941C6",
        light: "#F4F3FF",
        border: "#D9D6FE",
        icon: Flight,
    },
    manual: {
        label: "Manual",
        color: "#475467",
        light: "#F2F4F7",
        border: "#D0D5DD",
        icon: ReceiptLong,
    },
};

const TransportInvoiceCard = ({
                                  mode,
                                  data,
                                  totalRecords,
                                  currency = "EUR",
                              }) => {
    const config = MODE_CONFIG[mode] || MODE_CONFIG.manual;
    const Icon = config.icon;
    const share = totalRecords ? (data.count / totalRecords) * 100 : 0;

    return (
        <Card sx={{ ...cardSx, height: "100%" }}>
            <MDBox p={2.4}>
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <MDBox>
                        <MDTypography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ color: COLORS.subtext, letterSpacing: ".08em" }}
                        >
                            {`${financeText(config.label).toUpperCase()} ${financeText("SUPPLIER INVOICES")}`}
                        </MDTypography>
                        <MDTypography
                            variant="h4"
                            fontWeight="bold"
                            mt={0.8}
                            sx={{ color: COLORS.text }}
                        >
                            {formatNumber(data.count)}
                        </MDTypography>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {share.toFixed(1)}% {financeText("of filtered invoices")}
                        </MDTypography>
                    </MDBox>
                    <MDBox
                        width={46}
                        height={46}
                        borderRadius="8px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            color: config.color,
                            background: config.light,
                            border: `1px solid ${config.border}`,
                        }}
                    >
                        <Icon />
                    </MDBox>
                </MDBox>
                <Divider sx={{ my: 1.7 }} />
                <Grid container spacing={1.2}>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Net Payable")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: COLORS.amber }}
                        >
                            {formatCompact(data.netPayable, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Unpaid")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: COLORS.red }}
                        >
                            {formatCompact(data.unpaid, currency)}
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Approval")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: COLORS.green }}
                        >
                            {data.approvalRate.toFixed(1)}%
                        </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                            {financeText("Paid")}
                        </MDTypography>
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{ color: COLORS.blue }}
                        >
                            {data.paidCount}/{data.count}
                        </MDTypography>
                    </Grid>
                </Grid>
            </MDBox>
        </Card>
    );
};

const ModePayableChart = ({ summary, currency = "EUR" }) => {
    const max = Math.max(
        ...Object.values(summary).map((item) => item.netPayable),
        1,
    );

    return (
        <MDBox>
            {Object.entries(summary).map(([mode, data]) => {
                const config = MODE_CONFIG[mode] || MODE_CONFIG.manual;
                const Icon = config.icon;
                return (
                    <MDBox key={mode} mb={2.1}>
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={0.8}
                        >
                            <MDBox display="flex" alignItems="center" gap={0.8}>
                                <Icon
                                    sx={{ color: config.color, fontSize: "18px !important" }}
                                />
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText(config.label)}
                                </MDTypography>
                            </MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: config.color }}
                            >
                                {formatCompact(data.netPayable, currency)}
                            </MDTypography>
                        </MDBox>
                        <LinearProgress
                            variant="determinate"
                            value={(data.netPayable / max) * 100}
                            sx={{
                                height: 10,
                                borderRadius: "999px",
                                backgroundColor: "#EEF2F6",
                                "& .MuiLinearProgress-bar": {
                                    borderRadius: "999px",
                                    background: config.color,
                                },
                            }}
                        />
                        <MDBox display="flex" justifyContent="space-between" mt={0.55}>
                            <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                {data.count} {financeText("invoices")} · {data.approved} {financeText("approved")}
                            </MDTypography>
                            <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                sx={{ color: COLORS.red }}
                            >
                                {financeText("Unpaid")} {formatCompact(data.unpaid, currency)}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                );
            })}
        </MDBox>
    );
};

const initialInvoiceForm = {
    financeJobId: "",
    shipmentId: "",

    jobNo: "",
    shipmentNo: "",

    supplierName: "",
    supplierTaxNo: "",

    invoiceNo: "",
    invoiceDate: "",
    dueDate: "",

    totalAmount: "",
    vatAmount: "",
    netPayableAmount: "",

    currency: "EUR",
    uploadedFileUrl: "",

    approvalStatus: "Pending",
    paymentStatus: "Not Due",
    duplicateCheckStatus: "Clean",

    includedInMasterCost: false,
};

const SupplierInvoices = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const [invoices, setInvoices] = useState([]);
    const [businessPartners, setBusinessPartners] = useState([]);

    const [loading, setLoading] = useState(true);

    const [lastUpdated, setLastUpdated] = useState(new Date());

    const [search, setSearch] = useState("");

    const [approvalStatus, setApprovalStatus] = useState("all");

    const [paymentStatus, setPaymentStatus] = useState("all");

    const [duplicateStatus, setDuplicateStatus] = useState("all");
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const todayValue = `${currentYear}-${String(currentDate.getMonth() + 1).padStart(2, "0")}-${String(currentDate.getDate()).padStart(2, "0")}`;
    const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
    const [endDate, setEndDate] = useState(todayValue);
    const [selectedParty, setSelectedParty] = useState(null);
    const [transportMode, setTransportMode] = useState("all");
    const [invoiceOrigin, setInvoiceOrigin] = useState("all");
    const [documentType, setDocumentType] = useState("all");
    const [returnStatus, setReturnStatus] = useState("all");
    const [currencyFilter, setCurrencyFilter] = useState("all");
    const [geographicScope, setGeographicScope] = useState("all");
    const [exchangeRates, setExchangeRates] = useState(null);
    // Display currency: which currency the aggregated KPIs / charts / CSV
    // totals are converted into before being shown. Deliberately separate from
    // `currencyFilter` above, which only decides WHICH RECORDS are listed.
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");

    // Every aggregated figure below (hero stat, KPI tiles, transport-mode
    // cards, summary chips, CSV export) is converted into this currency before
    // display -- see `convertCostAmount`, which always converts an invoice's
    // amount out of its own currency into this one.
    //
    // It follows the user's explicit display-currency selection and is NOT
    // derived from `currencyFilter` any more: narrowing the visible records to
    // one currency and choosing what currency to report totals in are two
    // independent decisions.
    const reportingCurrency = normalizeCurrencyCode(selectedCurrency);

    const [modalOpen, setModalOpen] = useState(false);

    const [invoiceForm, setInvoiceForm] = useState(initialInvoiceForm);

    const token = localStorage.getItem("token");

    const fetchWithAuth = useCallback(
        async (path) => {
            const response = await fetch(`${API_URL}${path}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`${path} fetch failed`);
            }

            const data = await response.json();

            const candidates = [
                data,
                data?.data,
                data?.supplierInvoices,
                data?.supplier_invoices,
                data?.returnInvoices,
                data?.return_invoices,
                data?.invoices,
                data?.results,
                data?.records,
                data?.data?.supplierInvoices,
                data?.data?.supplier_invoices,
                data?.data?.returnInvoices,
                data?.data?.return_invoices,
                data?.data?.invoices,
                data?.data?.results,
                data?.data?.records,
            ];

            return candidates.find(Array.isArray) || [];
        },
        [token],
    );

    const fetchSupplierInvoices = useCallback(async () => {
        try {
            setLoading(true);

            const [supplierInvoiceData, manualPurchaseInvoices, returnInvoiceData] =
                await Promise.all([
                    fetchWithAuth("/finance/supplier-invoices"),
                    fetchWithAuth(
                        "/finance/manual-invoices?invoiceType=purchase&limit=200",
                    ).catch((manualInvoiceError) => {
                        console.warn(
                            "Manual purchase invoices could not be loaded:",
                            manualInvoiceError,
                        );
                        return [];
                    }),
                    fetchWithAuth(
                        "/finance/return-invoices?invoiceType=purchase&limit=200",
                    ).catch((returnInvoiceError) => {
                        console.warn(
                            "Purchase return invoices could not be loaded:",
                            returnInvoiceError,
                        );
                        return [];
                    }),
                ]);
            const uniqueInvoices = new Map();

            [...manualPurchaseInvoices, ...supplierInvoiceData]
                .filter(
                    (invoice) =>
                        !isReturnInvoice(invoice) &&
                        (!invoice?.invoiceType ||
                            String(invoice.invoiceType).toLowerCase() === "purchase"),
                )
                .forEach((invoice, index) => {
                    const key =
                        invoice?._id ||
                        invoice?.id ||
                        invoice?.invoiceNo ||
                        invoice?.invoice_no ||
                        `supplier-invoice-${index}`;
                    const existing = uniqueInvoices.get(String(key));
                    uniqueInvoices.set(String(key), {
                        ...(existing || {}),
                        ...invoice,
                        invoiceItems:
                            invoice?.invoiceItems ||
                            invoice?.invoice_items ||
                            existing?.invoiceItems ||
                            existing?.invoice_items ||
                            [],
                    });
                });

            const originalInvoices = Array.from(uniqueInvoices.values());
            const originalById = new Map();
            const originalByNo = new Map();

            originalInvoices.forEach((invoice) => {
                const id = getEntityId(
                    invoice?._id ||
                    invoice?.id ||
                    invoice?.invoiceId ||
                    invoice?.invoice_id,
                );
                const invoiceNo = normalizeLookupKey(getInvoiceNo(invoice));

                if (id) originalById.set(id, invoice);
                if (invoiceNo) originalByNo.set(invoiceNo, invoice);
            });

            const uniqueReturnInvoices = Array.from(
                new Map(
                    returnInvoiceData
                        .filter(
                            (returnInvoice) =>
                                !returnInvoice?.invoiceType ||
                                String(returnInvoice.invoiceType).toLowerCase() === "purchase",
                        )
                        .map((returnInvoice, index) => [
                            getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                            `purchase-return-${index}`,
                            returnInvoice,
                        ]),
                ).values(),
            );
            const returnsByOriginalId = new Map();
            const returnsByOriginalNo = new Map();

            uniqueReturnInvoices.forEach((returnInvoice) => {
                const originalId = getOriginalInvoiceId(returnInvoice);
                const originalNo = normalizeLookupKey(
                    getOriginalInvoiceNo(returnInvoice),
                );

                if (originalId) {
                    returnsByOriginalId.set(originalId, [
                        ...(returnsByOriginalId.get(originalId) || []),
                        returnInvoice,
                    ]);
                }

                if (originalNo) {
                    returnsByOriginalNo.set(originalNo, [
                        ...(returnsByOriginalNo.get(originalNo) || []),
                        returnInvoice,
                    ]);
                }
            });

            const enrichedOriginalInvoices = originalInvoices.map((invoice) => {
                const id = getEntityId(
                    invoice?._id ||
                    invoice?.id ||
                    invoice?.invoiceId ||
                    invoice?.invoice_id,
                );
                const invoiceNo = normalizeLookupKey(getInvoiceNo(invoice));
                const linkedReturns = [
                    ...(returnsByOriginalId.get(id) || []),
                    ...(returnsByOriginalNo.get(invoiceNo) || []),
                ];
                const uniqueLinkedReturns = Array.from(
                    new Map(
                        linkedReturns.map((returnInvoice, index) => [
                            getEntityId(returnInvoice?._id || returnInvoice?.id) ||
                            `${id || invoiceNo}-return-${index}`,
                            returnInvoice,
                        ]),
                    ).values(),
                );

                return {
                    ...invoice,
                    documentType: "invoice",
                    returnInvoices: uniqueLinkedReturns,
                };
            });

            const normalizedReturnInvoices = uniqueReturnInvoices.map(
                (returnInvoice, index) => {
                    const originalId = getOriginalInvoiceId(returnInvoice);
                    const originalNo = normalizeLookupKey(
                        getOriginalInvoiceNo(returnInvoice),
                    );
                    const originalInvoice =
                        originalById.get(originalId) ||
                        originalByNo.get(originalNo) ||
                        null;
                    const returnInvoiceId = getEntityId(
                        returnInvoice?._id || returnInvoice?.id,
                    );
                    const returnAmount = getRawReturnAmount(returnInvoice);

                    return {
                        ...(originalInvoice || {}),
                        ...returnInvoice,
                        _id: `return-${returnInvoiceId || index}`,
                        returnInvoiceId,
                        originalInvoiceId: originalId,
                        originalInvoice,
                        documentType: "return_invoice",
                        invoiceType: "purchase",
                        invoiceOrigin:
                            returnInvoice?.invoiceOrigin ||
                            returnInvoice?.invoice_origin ||
                            originalInvoice?.invoiceOrigin ||
                            originalInvoice?.invoice_origin ||
                            "manual",
                        invoiceNo:
                            returnInvoice?.returnInvoiceNumber ||
                            returnInvoice?.return_invoice_number ||
                            returnInvoice?.returnInvoiceNo ||
                            returnInvoice?.return_invoice_no ||
                            returnInvoice?.invoiceNo ||
                            `RETURN-${index + 1}`,
                        supplierName:
                            returnInvoice?.supplierName ||
                            returnInvoice?.supplier_name ||
                            originalInvoice?.supplierName ||
                            originalInvoice?.supplier_name ||
                            "N/A",
                        customerName:
                            returnInvoice?.customerName ||
                            returnInvoice?.customer_name ||
                            originalInvoice?.customerName ||
                            originalInvoice?.customer_name ||
                            "N/A",
                        currency: normalizeCurrencyCode(
                            returnInvoice?.currency ||
                            returnInvoice?.costCurrency ||
                            originalInvoice?.currency ||
                            originalInvoice?.costCurrency,
                        ),
                        costCurrency: normalizeCurrencyCode(
                            returnInvoice?.costCurrency ||
                            returnInvoice?.currency ||
                            originalInvoice?.costCurrency ||
                            originalInvoice?.currency,
                        ),
                        totalAmount: returnAmount,
                        netPayableAmount: returnAmount,
                        approvalStatus: getReturnStatus(returnInvoice),
                        paymentStatus: "Not Applicable",
                        duplicateCheckStatus: "Not Applicable",
                        includedInMasterCost: false,
                    };
                },
            );

            setInvoices([...enrichedOriginalInvoices, ...normalizedReturnInvoices]);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Supplier invoices fetch error:", error);

            setInvoices([]);
        } finally {
            setLoading(false);
        }
    }, [fetchWithAuth]);

    const fetchBusinessPartners = useCallback(async () => {
        const firstAvailable = async (paths) => {
            for (const path of paths) {
                try {
                    return await fetchWithAuth(path);
                } catch (error) {
                    console.warn(`${path} lookup failed`, error);
                }
            }
            return [];
        };
        const [customers, providers, agents] = await Promise.all([
            firstAvailable(["/customer", "/customers"]),
            firstAvailable(["/serviceProvider", "/service-provider"]),
            firstAvailable(["/globalAgent", "/global-agent"]),
        ]);
        setBusinessPartners([...customers, ...providers, ...agents]);
    }, [fetchWithAuth]);

    useEffect(() => {
        fetchSupplierInvoices();
        fetchBusinessPartners();
    }, [fetchSupplierInvoices, fetchBusinessPartners]);

    useEffect(() => {
        let active = true;

        const loadRates = async () => {
            try {
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/USD",
                );

                if (!response.ok) throw new Error("Exchange rates fetch failed");

                const data = await response.json();

                if (active) setExchangeRates(data?.rates || null);
            } catch (error) {
                console.error("Exchange rates fetch error:", error);

                if (active) setExchangeRates(null);
            }
        };

        loadRates();

        return () => {
            active = false;
        };
    }, []);

    const convertCostAmount = useCallback(
        (invoice, amount, targetCurrency = reportingCurrency) => {
            const value = toNumber(amount);
            const from = getCurrency(invoice);
            const to = normalizeCurrencyCode(targetCurrency);

            if (from === to) return value;
            if (!exchangeRates?.[from] || !exchangeRates?.[to]) return null;

            return (value / exchangeRates[from]) * exchangeRates[to];
        },
        [exchangeRates, reportingCurrency],
    );

    const partyCountryLookup = useMemo(() => {
        const lookup = new Map();
        businessPartners.forEach((record) => {
            const item = record?.attributes || record;
            const countries = [
                item?.customerCountry,
                item?.serviceProviderCountry,
                item?.globalAgentCountry,
                ...(Array.isArray(item?.servicesCountry) ? item.servicesCountry : []),
            ].filter(Boolean);
            [
                record?.id,
                item?._id,
                item?.id,
                item?.name,
                item?.nameKey,
                item?.companyName,
                item?.customerNo,
                item?.serviceProviderNo,
                item?.globalAgentNo,
            ]
                .map(normalizeLookupKey)
                .filter(Boolean)
                .forEach((key) =>
                    lookup.set(key, [...(lookup.get(key) || []), ...countries]),
                );
        });
        return lookup;
    }, [businessPartners]);

    useEffect(() => {
        const state = location.state || {};

        if (
            state.financeJobId ||
            state.shipmentId ||
            state.jobNo ||
            state.shipmentNo ||
            state.openInvoiceModal ||
            state.openSupplierInvoiceModal
        ) {
            setInvoiceForm((previous) => ({
                ...previous,

                financeJobId: state.financeJobId || previous.financeJobId,

                shipmentId: state.shipmentId || previous.shipmentId,

                jobNo: state.jobNo || state.shipmentNo || previous.jobNo,

                shipmentNo: state.shipmentNo || state.jobNo || previous.shipmentNo,
            }));

            if (state.openInvoiceModal || state.openSupplierInvoiceModal) {
                setModalOpen(true);
            }

            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const statusOptions = useMemo(() => {
        const unique = (camel, snake, fallback) => {
            const values = invoices
                .map((item) => String(getValue(item, camel, snake, fallback)).trim())
                .filter(Boolean);

            return ["all", ...Array.from(new Set(values))];
        };

        return {
            approval: unique("approvalStatus", "approval_status", "Pending"),

            payment: unique("paymentStatus", "payment_status", "Not Due"),

            duplicate: unique(
                "duplicateCheckStatus",
                "duplicate_check_status",
                "Clean",
            ),
        };
    }, [invoices]);

    const returnStatusOptions = useMemo(
        () => [
            "all",
            ...Array.from(
                new Set(invoices.map((invoice) => getReturnSummary(invoice).status)),
            ).filter(Boolean),
        ],
        [invoices],
    );

    const partyOptions = useMemo(() => {
        const unique = new Map();

        invoices.forEach((invoice) => {
            const customerName = getCustomerName(invoice);
            const supplierName = getSupplierName(invoice);

            if (customerName && customerName !== "N/A") {
                unique.set(`CUSTOMER-${customerName}`, {
                    type: "CUSTOMER",
                    name: customerName,
                    label: `Customer - ${customerName}`,
                });
            }

            if (supplierName && supplierName !== "N/A") {
                unique.set(`SUPPLIER-${supplierName}`, {
                    type: "SUPPLIER",
                    name: supplierName,
                    label: `Supplier - ${supplierName}`,
                });
            }
        });

        return Array.from(unique.values()).sort((a, b) =>
            a.label.localeCompare(b.label),
        );
    }, [invoices]);

    const transportModeOptions = useMemo(
        () => Array.from(new Set(invoices.map(getTransportMode))).sort(),
        [invoices],
    );

    const currencyOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    invoices.map((invoice) => String(getCurrency(invoice)).toUpperCase()),
                ),
            ).sort(),
        [invoices],
    );

    const filteredInvoices = useMemo(() => {
        const searchText = search.toLowerCase().trim();
        const rangeStart = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const rangeEnd = endDate ? new Date(`${endDate}T23:59:59`) : null;

        return invoices.filter((invoice) => {
            const jobNo = String(getShipmentNo(invoice)).toLowerCase();

            const supplier = String(getSupplierName(invoice)).toLowerCase();
            const customer = String(getCustomerName(invoice)).toLowerCase();
            const invoiceDate = getDateValue(invoice);

            const invoiceNo = String(getInvoiceNo(invoice)).toLowerCase();
            const originalInvoiceNo = String(
                getOriginalInvoiceNo(invoice),
            ).toLowerCase();

            const taxNo = String(
                getValue(invoice, "supplierTaxNo", "supplier_tax_no", ""),
            ).toLowerCase();

            const currentApproval = String(getApprovalStatus(invoice)).toLowerCase();

            const currentPayment = String(getPaymentStatus(invoice)).toLowerCase();

            const currentDuplicate = String(
                getDuplicateStatus(invoice),
            ).toLowerCase();

            const matchesSearch =
                !searchText ||
                jobNo.includes(searchText) ||
                supplier.includes(searchText) ||
                customer.includes(searchText) ||
                invoiceNo.includes(searchText) ||
                originalInvoiceNo.includes(searchText) ||
                taxNo.includes(searchText);

            const matchesStartDate =
                !rangeStart || (invoiceDate && invoiceDate >= rangeStart);
            const matchesEndDate =
                !rangeEnd || (invoiceDate && invoiceDate <= rangeEnd);
            const matchesParty =
                !selectedParty ||
                (selectedParty.type === "CUSTOMER"
                    ? getCustomerName(invoice) === selectedParty.name
                    : getSupplierName(invoice) === selectedParty.name);
            const matchesTransportMode =
                transportMode === "all" || getTransportMode(invoice) === transportMode;
            const matchesInvoiceOrigin =
                invoiceOrigin === "all" || getInvoiceOrigin(invoice) === invoiceOrigin;
            const matchesDocumentType =
                documentType === "all" || getDocumentType(invoice) === documentType;
            const matchesReturnStatus =
                returnStatus === "all" ||
                normalizeText(getReturnSummary(invoice).status) ===
                normalizeText(returnStatus);
            const matchesCurrency =
                currencyFilter === "all" ||
                String(getCurrency(invoice)).toUpperCase() === currencyFilter;
            const matchesGeographicScope =
                geographicScope === "all" ||
                getGeographicScope(invoice, partyCountryLookup) === geographicScope;

            const matchesApproval =
                approvalStatus === "all" ||
                currentApproval === approvalStatus.toLowerCase();

            const matchesPayment =
                paymentStatus === "all" ||
                currentPayment === paymentStatus.toLowerCase();

            const matchesDuplicate =
                duplicateStatus === "all" ||
                currentDuplicate === duplicateStatus.toLowerCase();

            return (
                matchesSearch &&
                matchesStartDate &&
                matchesEndDate &&
                matchesParty &&
                matchesTransportMode &&
                matchesInvoiceOrigin &&
                matchesDocumentType &&
                matchesReturnStatus &&
                matchesCurrency &&
                matchesGeographicScope &&
                matchesApproval &&
                matchesPayment &&
                matchesDuplicate
            );
        });
    }, [
        invoices,
        search,
        approvalStatus,
        paymentStatus,
        duplicateStatus,
        startDate,
        endDate,
        selectedParty,
        transportMode,
        invoiceOrigin,
        documentType,
        returnStatus,
        currencyFilter,
        geographicScope,
        partyCountryLookup,
    ]);

    const stats = useMemo(() => {
        const regularInvoices = filteredInvoices.filter(
            (item) => !isReturnInvoice(item),
        );
        const uniqueFinanceJobs = getUniqueFinanceJobs(regularInvoices);
        const returnDocuments = filteredInvoices.filter(isReturnInvoice);
        const totalAmount = regularInvoices.reduce(
            (sum, item) => sum + (convertCostAmount(item, getTotalAmount(item)) ?? 0),
            0,
        );

        const netPayable = regularInvoices.reduce(
            (sum, item) =>
                sum + (convertCostAmount(item, getNetInvoicePayable(item)) ?? 0),
            0,
        );

        const issuedReturnAmount = regularInvoices.length
            ? regularInvoices.reduce(
                (sum, item) =>
                    sum +
                    (convertCostAmount(item, getReturnSummary(item).issuedAmount) ?? 0),
                0,
            )
            : returnDocuments
                .filter((item) => getReturnStatus(item).toLowerCase() === "issued")
                .reduce(
                    (sum, item) =>
                        sum + (convertCostAmount(item, getRawReturnAmount(item)) ?? 0),
                    0,
                );

        const approvedCount = regularInvoices.filter((item) =>
            getApprovalStatus(item).toLowerCase().includes("approved"),
        ).length;

        const pendingCount = regularInvoices.filter((item) =>
            getApprovalStatus(item).toLowerCase().includes("pending"),
        ).length;

        const overdueCount = regularInvoices.filter(
            (item) =>
                getNetInvoicePayable(item) > 0 &&
                getOverdueDays(
                    getValue(item, "dueDate", "due_date"),
                    getPaymentStatus(item),
                ) > 0,
        ).length;

        const duplicateCount = regularInvoices.filter((item) =>
            getDuplicateStatus(item).toLowerCase().includes("duplicate"),
        ).length;

        const approvalRate = regularInvoices.length
            ? (approvedCount / regularInvoices.length) * 100
            : 0;

        // Sum the actual paidAmount/remaining-balance per invoice instead of
        // gating on the paymentStatus string: `.includes("paid")` also
        // matched "Unpaid", and even an exact match ignored partial
        // payments — an invoice with paymentStatus "Partial" contributed
        // its full remaining balance to *both* totals depending on which
        // branch it fell into, instead of splitting between what's already
        // paid and what's still outstanding.
        const paidInvoiceTotal = regularInvoices.reduce(
            (sum, item) =>
                sum +
                (convertCostAmount(
                    item,
                    firstDefinedNumber(item?.paidAmount, item?.paid_amount, 0),
                ) ?? 0),
            0,
        );

        const unpaidInvoiceTotal = regularInvoices.reduce(
            (sum, item) =>
                sum +
                (convertCostAmount(
                    item,
                    Math.max(
                        getNetInvoicePayable(item) -
                            firstDefinedNumber(item?.paidAmount, item?.paid_amount, 0),
                        0,
                    ),
                ) ?? 0),
            0,
        );

        const additionalSupplierCost = regularInvoices.reduce(
            (sum, item) =>
                sum + (convertCostAmount(item, getNetSupplierInvoiceCost(item)) ?? 0),
            0,
        );

        const includedInMasterCount = regularInvoices.filter(
            isIncludedInMasterCost,
        ).length;

        const masterTotalCost = uniqueFinanceJobs.reduce(
            (sum, item) =>
                sum +
                (convertCostAmount(
                    item,
                    Math.max(
                        getMasterTotalCost(item) - getReturnSummary(item).issuedAmount,
                        0,
                    ),
                ) ?? 0),
            0,
        );

        const buyingRateTotal = uniqueFinanceJobs.reduce(
            (sum, item) => sum + (convertCostAmount(item, getBuyingRate(item)) ?? 0),
            0,
        );

        return {
            count: regularInvoices.length,
            returnCount: returnDocuments.length,

            totalAmount,
            netPayable,
            issuedReturnAmount,

            approvedCount,
            pendingCount,

            overdueCount,
            duplicateCount,

            approvalRate,

            paidInvoiceTotal,
            unpaidInvoiceTotal,

            additionalSupplierCost,
            includedInMasterCount,

            masterTotalCost,
            buyingRateTotal,
        };
    }, [filteredInvoices, convertCostAmount]);

    const transportSummary = useMemo(() => {
        const initial = {
            ocean: {
                count: 0,
                total: 0,
                netPayable: 0,
                unpaid: 0,
                approved: 0,
                paidCount: 0,
                approvalRate: 0,
            },
            road: {
                count: 0,
                total: 0,
                netPayable: 0,
                unpaid: 0,
                approved: 0,
                paidCount: 0,
                approvalRate: 0,
            },
            air: {
                count: 0,
                total: 0,
                netPayable: 0,
                unpaid: 0,
                approved: 0,
                paidCount: 0,
                approvalRate: 0,
            },
            manual: {
                count: 0,
                total: 0,
                netPayable: 0,
                unpaid: 0,
                approved: 0,
                paidCount: 0,
                approvalRate: 0,
            },
        };

        filteredInvoices
            .filter((item) => !isReturnInvoice(item))
            .forEach((item) => {
                const mode = getTransportMode(item);
                const total = convertCostAmount(item, getNetInvoiceTotal(item)) ?? 0;
                const net = convertCostAmount(item, getNetInvoicePayable(item)) ?? 0;
                const approved = getApprovalStatus(item)
                    .toLowerCase()
                    .includes("approved");
                // Exact match: "unpaid" also contains the substring "paid".
                const paid = getPaymentStatus(item).toLowerCase() === "paid";
                const paidAmount = firstDefinedNumber(item?.paidAmount, item?.paid_amount, 0);
                const remaining = Math.max(
                    net - (convertCostAmount(item, paidAmount) ?? 0),
                    0,
                );

                const target = initial[mode] || initial.manual;

                target.count += 1;
                target.total += total;
                target.netPayable += net;
                if (!paid) target.unpaid += remaining;
                if (approved) target.approved += 1;
                if (paid) target.paidCount += 1;
            });

        Object.values(initial).forEach((item) => {
            item.approvalRate = item.count ? (item.approved / item.count) * 100 : 0;
        });

        return initial;
    }, [filteredInvoices, convertCostAmount]);

    const shipmentTypeSummary = useMemo(() => {
        const counts = {};
        filteredInvoices
            .filter((item) => !isReturnInvoice(item))
            .forEach((item) => {
                const type = getShipmentType(item);
                counts[type] = (counts[type] || 0) + 1;
            });
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8);
    }, [filteredInvoices]);

    const openReturnInvoice = (returnInvoice) => {
        const returnInvoiceId = getEntityId(
            returnInvoice?.returnInvoiceId || returnInvoice?._id || returnInvoice?.id,
        ).replace(/^return-/, "");

        if (!returnInvoiceId) {
            appAlert(financeText("Return Invoice ID not found!"));
            return;
        }

        navigate(`/invoice/finance/return-invoices/${returnInvoiceId}`, {
            state: {
                returnInvoice,
                originalInvoice: returnInvoice?.originalInvoice || null,
                originalInvoiceId: getOriginalInvoiceId(returnInvoice),
            },
        });
    };

    const openOriginalInvoice = (returnInvoice) => {
        const originalInvoice = returnInvoice?.originalInvoice || null;
        const originalInvoiceId =
            getOriginalInvoiceId(returnInvoice) ||
            getEntityId(originalInvoice?._id || originalInvoice?.id);

        if (!originalInvoiceId) {
            appAlert(financeText("Original Invoice ID not found!"));
            return;
        }

        navigate(`/invoice/finance/jobs/${originalInvoiceId}`, {
            state: {
                job: originalInvoice,
                invoiceId: originalInvoiceId,
                financeJobId: originalInvoiceId,
                invoiceOrigin: getInvoiceOrigin(originalInvoice || returnInvoice),
            },
        });
    };

    const openLatestReturnInvoice = (invoice) => {
        const latestReturnInvoice = getReturnSummary(invoice).latestReturnInvoice;

        if (!latestReturnInvoice) {
            appAlert(financeText("No linked return invoice was found."));
            return;
        }

        openReturnInvoice(latestReturnInvoice);
    };

    const resolveSupplierInvoice = async (invoice) => {
        if (isReturnInvoice(invoice)) return invoice;

        const recordIds = [
            invoice?.financeJobId,
            invoice?.finance_job_id,
            invoice?.jobFinanceId,
            invoice?.job_finance_id,
            invoice?._id,
            invoice?.id,
        ].filter(Boolean);
        const uniqueIds = [...new Set(recordIds.map(String))];

        for (const recordId of uniqueIds) {
            const detailPaths = [
                `/finance/manual-invoices/${recordId}`,
                `/finance/supplier-invoices/${recordId}`,
                `/invoice/${recordId}`,
                `/finance/jobs/${recordId}`,
            ];

            for (const path of detailPaths) {
                try {
                    const response = await fetch(`${API_URL}${path}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (!response.ok) continue;

                    const payload = await response.json();
                    const detail =
                        payload?.data?.invoice ||
                        payload?.data?.job ||
                        payload?.data?.data ||
                        payload?.invoice ||
                        payload?.job ||
                        payload?.data ||
                        payload;

                    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
                        return {
                            ...invoice,
                            ...detail,
                            invoiceItems:
                                detail?.invoiceItems ||
                                detail?.invoice_items ||
                                invoice?.invoiceItems ||
                                invoice?.invoice_items ||
                                [],
                        };
                    }
                } catch (error) {
                    console.warn(`${path} supplier invoice detail lookup failed`, error);
                }
            }
        }

        return invoice;
    };

    const openFinanceDetail = async (invoice) => {
        if (isReturnInvoice(invoice)) {
            openReturnInvoice(invoice);
            return;
        }

        const resolvedInvoice = await resolveSupplierInvoice(invoice);
        const manualInvoiceId =
            resolvedInvoice?._id ||
            resolvedInvoice?.id ||
            resolvedInvoice?.invoiceId ||
            resolvedInvoice?.invoice_id;

        if (getInvoiceOrigin(resolvedInvoice) === "manual") {
            if (!manualInvoiceId) {
                appAlert(financeText("Manual Invoice ID not found!"));
                return;
            }

            navigate(`/invoice/finance/jobs/${manualInvoiceId}`, {
                state: {
                    job: resolvedInvoice,
                    financeJobId: manualInvoiceId,
                    invoiceOrigin: "manual",
                },
            });
            return;
        }

        const financeJobId =
            resolvedInvoice?.financeJobId ||
            resolvedInvoice?.finance_job_id ||
            resolvedInvoice?.jobFinanceId ||
            resolvedInvoice?.job_finance_id;

        if (!financeJobId) {
            appAlert(
                financeText(
                    "Finance Job ID not found. Supplier invoice must be linked with financeJobId."
                ),
            );

            return;
        }

        navigate(`/invoice/finance/jobs/${financeJobId}`, {
            state: {
                job: resolvedInvoice,
                financeJobId,
            },
        });
    };

    const openDocument = (invoice) => {
        const url =
            invoice?.uploadedFileUrl ||
            invoice?.uploaded_file_url ||
            invoice?.pdfUrl ||
            invoice?.pdf_url;

        if (!url) {
            appAlert(financeText("Document not found!"));

            return;
        }

        window.open(url, "_blank", "noopener,noreferrer");
    };

    const checkDuplicate = (payload) => {
        const invoiceNo = String(payload?.invoiceNo || "")
            .trim()
            .toLowerCase();

        const supplier = String(payload?.supplierName || "")
            .trim()
            .toLowerCase();

        if (!invoiceNo || !supplier) {
            return "Clean";
        }

        const exists = invoices
            .filter((item) => !isReturnInvoice(item))
            .some((item) => {
                const currentInvoiceNo = String(getInvoiceNo(item))
                    .trim()
                    .toLowerCase();

                const currentSupplier = String(getSupplierName(item))
                    .trim()
                    .toLowerCase();

                return currentInvoiceNo === invoiceNo && currentSupplier === supplier;
            });

        return exists ? "Potential Duplicate" : "Clean";
    };

    const openUploadModal = () => {
        const state = location.state || {};

        setInvoiceForm({
            ...initialInvoiceForm,

            financeJobId: state.financeJobId || "",

            shipmentId: state.shipmentId || "",

            jobNo: state.jobNo || state.shipmentNo || "",

            shipmentNo: state.shipmentNo || state.jobNo || "",
        });

        setModalOpen(true);
    };

    const saveSupplierInvoice = async () => {
        if (!invoiceForm.supplierName.trim()) {
            appAlert(financeText("Supplier name is required."));

            return;
        }

        if (!invoiceForm.invoiceNo.trim()) {
            appAlert(financeText("Invoice no is required."));

            return;
        }

        const totalAmount = toNumber(invoiceForm.totalAmount);

        if (!totalAmount) {
            appAlert(financeText("Total amount is required."));

            return;
        }

        const vatAmount = toNumber(invoiceForm.vatAmount);

        const netPayableAmount = firstDefinedNumber(
            invoiceForm.netPayableAmount,
            totalAmount,
        );

        const payload = {
            ...invoiceForm,
            currency: normalizeCurrencyCode(invoiceForm.currency),
            costCurrency: normalizeCurrencyCode(invoiceForm.currency),

            financeJobId: invoiceForm.financeJobId || null,

            shipmentId: invoiceForm.shipmentId || null,

            jobNo: invoiceForm.jobNo || invoiceForm.shipmentNo,

            shipmentNo: invoiceForm.shipmentNo || invoiceForm.jobNo,

            totalAmount,
            vatAmount,
            netPayableAmount,

            includedInMasterCost: Boolean(invoiceForm.includedInMasterCost),

            duplicateCheckStatus: checkDuplicate(invoiceForm),

            createdAt: new Date().toISOString(),
        };

        try {
            const response = await fetch(`${API_URL}/finance/supplier-invoices`, {
                method: "POST",

                headers: {
                    Authorization: `Bearer ${token}`,

                    "Content-Type": "application/json",
                },

                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);

                throw new Error(errorData?.message || "Supplier invoice create failed");
            }

            const result = await response.json();

            setInvoices((previous) => [result?.data || result, ...previous]);

            setModalOpen(false);
        } catch (error) {
            console.error("Supplier invoice save error:", error);

            appAlert(error.message || financeText("Supplier invoice could not be saved."));
        }
    };

    const approveInvoice = async (invoice) => {
        if (isReturnInvoice(invoice)) {
            openReturnInvoice(invoice);
            return;
        }

        const id = invoice?._id || invoice?.id;

        if (!id) {
            appAlert(financeText("Invoice ID not found!"));

            return;
        }

        try {
            const response = await fetch(
                `${API_URL}/finance/supplier-invoices/${id}`,
                {
                    method: "PATCH",

                    headers: {
                        Authorization: `Bearer ${token}`,

                        "Content-Type": "application/json",
                    },

                    body: JSON.stringify({
                        approvalStatus: "Approved",

                        approvedAt: new Date().toISOString(),
                    }),
                },
            );

            if (!response.ok) {
                throw new Error("Approve failed");
            }

            const result = await response.json();

            const updated = result?.data || result;

            // Merge onto the existing row instead of replacing it outright:
            // the server response has no `returnInvoices` field (that's a
            // client-only enrichment built in fetchSupplierInvoices), so a
            // full replacement was silently dropping it and making the net
            // payable jump back up to the gross amount right after Approve.
            setInvoices((previous) =>
                previous.map((item) =>
                    (item?._id || item?.id) === id ? { ...item, ...updated } : item,
                ),
            );
        } catch (error) {
            console.error("Supplier invoice approve error:", error);

            appAlert(financeText("Approve could not be saved."));
        }
    };

    const markPaid = async (invoice) => {
        if (isReturnInvoice(invoice)) {
            openReturnInvoice(invoice);
            return;
        }

        if (getNetInvoicePayable(invoice) <= 0) {
            appAlert(financeText("This supplier invoice has no payable balance after returns."));
            return;
        }

        const id = invoice?._id || invoice?.id;

        if (!id) {
            appAlert(financeText("Invoice ID not found!"));

            return;
        }

        try {
            const response = await fetch(
                `${API_URL}/finance/supplier-invoices/${id}`,
                {
                    method: "PATCH",

                    headers: {
                        Authorization: `Bearer ${token}`,

                        "Content-Type": "application/json",
                    },

                    body: JSON.stringify({
                        paymentStatus: "Paid",

                        paidAt: new Date().toISOString(),

                        // Was never sent, so paidAmount stayed at its old
                        // (usually 0) value in the DB despite the invoice
                        // reading "Paid" everywhere in this UI.
                        paidAmount: getNetInvoicePayable(invoice),
                    }),
                },
            );

            if (!response.ok) {
                throw new Error("Mark paid failed");
            }

            const result = await response.json();

            const updated = result?.data || result;

            setInvoices((previous) =>
                previous.map((item) =>
                    (item?._id || item?.id) === id ? { ...item, ...updated } : item,
                ),
            );
        } catch (error) {
            console.error("Supplier invoice mark paid error:", error);

            appAlert(financeText("Payment status could not be saved."));
        }
    };

    const resetFilters = () => {
        setSearch("");
        setApprovalStatus("all");
        setPaymentStatus("all");
        setDuplicateStatus("all");
        setStartDate(`${currentYear}-01-01`);
        setEndDate(todayValue);
        setSelectedParty(null);
        setTransportMode("all");
        setInvoiceOrigin("all");
        setDocumentType("all");
        setReturnStatus("all");
        setCurrencyFilter("all");
        setGeographicScope("all");
    };

    const exportCsv = () => {
        const headers = [
            "Shipment",
            "Invoice No",
            "Supplier",
            "Customer",
            "Geographic Scope",
            "Transport Mode",
            "Invoice Origin",
            "Document Type",
            "Original Invoice No",
            "Return Status",
            "Issued Return Amount",
            "Cost Currency",
            "Reporting Currency",
            "Tax No",
            "Invoice Date",
            "Due Date",
            "Total Amount",
            "VAT",
            "Gross Payable",
            "Net Payable After Return",
            "Buying Rate",
            "Master Total Cost",
            "Included In Master Cost",
            "Additional Supplier Cost",
            "Approval",
            "Payment",
            "Duplicate",
        ];

        const rows = filteredInvoices.map((invoice) => [
            getShipmentNo(invoice),

            getInvoiceNo(invoice),

            getSupplierName(invoice),

            getCustomerName(invoice),

            getGeographicLabel(invoice, partyCountryLookup),

            MODE_CONFIG[getTransportMode(invoice)]?.label ||
            getTransportMode(invoice),

            getInvoiceOrigin(invoice) === "manual" ? "Manual" : "TMS",

            isReturnInvoice(invoice) ? "Return Invoice" : "Supplier Invoice",

            getOriginalInvoiceNo(invoice),

            getReturnSummary(invoice).status,

            convertCostAmount(invoice, getReturnSummary(invoice).issuedAmount) ??
            "Rate unavailable",

            getCurrency(invoice),

            reportingCurrency,

            getValue(invoice, "supplierTaxNo", "supplier_tax_no", ""),

            formatDate(
                isReturnInvoice(invoice)
                    ? getDateValue(invoice)
                    : getValue(invoice, "invoiceDate", "invoice_date"),
            ),

            formatDate(getValue(invoice, "dueDate", "due_date")),

            convertCostAmount(invoice, getTotalAmount(invoice)) ?? "Rate unavailable",

            convertCostAmount(invoice, getVatAmount(invoice)) ?? "Rate unavailable",

            convertCostAmount(invoice, getNetPayable(invoice)) ?? "Rate unavailable",

            convertCostAmount(invoice, getNetInvoicePayable(invoice)) ??
            "Rate unavailable",

            convertCostAmount(invoice, getBuyingRate(invoice)) ?? "Rate unavailable",

            convertCostAmount(invoice, getMasterTotalCost(invoice)) ??
            "Rate unavailable",

            isIncludedInMasterCost(invoice) ? "Yes" : "No",

            convertCostAmount(invoice, getNetSupplierInvoiceCost(invoice)) ??
            "Rate unavailable",

            getApprovalStatus(invoice),

            getPaymentStatus(invoice),

            getDuplicateStatus(invoice),
        ]);

        const csv = [headers, ...rows]
            .map((row) =>
                row
                    .map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`)
                    .join(","),
            )
            .join("\n");

        const blob = new Blob([`\uFEFF${csv}`], {
            type: "text/csv;charset=utf-8;",
        });

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;

        const modeName = transportMode === "all" ? "all-modes" : transportMode;

        const scopeName =
            geographicScope === "all" ? "all-scopes" : geographicScope.toLowerCase();

        link.setAttribute(
            "download",
            `supplier-invoices-${modeName}-${scopeName}-${todayValue}.csv`,
        );

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };

    const tableData = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("SHIPMENT"),

                    accessor: "jobNo",

                    width: "12%",

                    Cell: ({ row }) => {
                        const invoice = row.original;

                        return (
                            <MDBox display="flex" alignItems="center" gap={1.2} py={0.35}>
                                <MDBox
                                    width={38}
                                    height={38}
                                    borderRadius="11px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    sx={{
                                        background:
                                            "#EEF4FF",

                                        color: COLORS.blue,

                                        border: "1px solid #D1E0FF",
                                    }}
                                >
                                    <ReceiptLong
                                        sx={{
                                            fontSize: "18px !important",
                                        }}
                                    />
                                </MDBox>

                                <MDBox minWidth={0}>
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        noWrap
                                        sx={{
                                            cursor: "pointer",

                                            color: COLORS.navy,

                                            display: "block",

                                            maxWidth: 145,

                                            "&:hover": {
                                                color: COLORS.blue,
                                            },
                                        }}
                                        onClick={() => openFinanceDetail(invoice)}
                                    >
                                        {getShipmentNo(invoice)}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        noWrap
                                        display="block"
                                        sx={{
                                            color: COLORS.subtext,
                                        }}
                                    >
                                        {financeText("Matched jobs")}:{" "}
                                        {getValue(
                                            invoice,
                                            "matchedJobCount",
                                            "matched_job_count",
                                            1,
                                        )}
                                    </MDTypography>

                                    <MDBox display="flex" gap={0.5} mt={0.6} flexWrap="wrap">
                                        <Chip
                                            size="small"
                                            label={financeText(
                                                (
                                                    MODE_CONFIG[getTransportMode(invoice)] ||
                                                    MODE_CONFIG.manual
                                                ).label
                                            )}
                                            sx={{
                                                height: 20,
                                                color: (
                                                    MODE_CONFIG[getTransportMode(invoice)] ||
                                                    MODE_CONFIG.manual
                                                ).color,
                                                background: (
                                                    MODE_CONFIG[getTransportMode(invoice)] ||
                                                    MODE_CONFIG.manual
                                                ).light,
                                                border: `1px solid ${(MODE_CONFIG[getTransportMode(invoice)] || MODE_CONFIG.manual).border}`,
                                                fontSize: "9px",
                                                fontWeight: 800,
                                            }}
                                        />
                                        <Chip
                                            size="small"
                                            label={getShipmentType(invoice)}
                                            sx={{
                                                height: 20,
                                                color: COLORS.navy,
                                                background: "#EEF2F6",
                                                fontSize: "9px",
                                                fontWeight: 800,
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        );
                    },
                },

                {
                    Header: financeText("INVOICE"),

                    accessor: "invoiceNo",

                    width: "12%",

                    Cell: ({ row }) => (
                        <MDBox>
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{
                                    color: COLORS.text,
                                }}
                            >
                                {getInvoiceNo(row.original)}
                            </MDTypography>

                            {isReturnInvoice(row.original) && (
                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{ color: COLORS.purple, fontWeight: 700 }}
                                >
                                    {financeText("Original")}: {getOriginalInvoiceNo(row.original) || "-"}
                                </MDTypography>
                            )}

                            <MDTypography
                                variant="caption"
                                display="block"
                                sx={{
                                    color: COLORS.subtext,
                                }}
                            >
                                {financeText("Tax No")}:{" "}
                                {getValue(
                                    row.original,
                                    "supplierTaxNo",
                                    "supplier_tax_no",
                                    "-",
                                )}
                            </MDTypography>
                        </MDBox>
                    ),
                },

                {
                    Header: financeText("DOCUMENT"),
                    accessor: "documentType",
                    width: "10%",
                    Cell: ({ row }) => {
                        const returnDocument = isReturnInvoice(row.original);

                        return (
                            <Chip
                                size="small"
                                label={financeText(returnDocument ? "Return Invoice" : "Supplier Invoice")}
                                sx={{
                                    color: returnDocument ? COLORS.purple : COLORS.navy,
                                    background: returnDocument ? COLORS.purpleLight : "#EEF2F6",
                                    border: `1px solid ${returnDocument ? "#D9D6FE" : "#D0D5DD"}`,
                                    fontWeight: 800,
                                }}
                            />
                        );
                    },
                },

                {
                    Header: financeText("SUPPLIER"),

                    accessor: "supplierName",

                    width: "14%",

                    Cell: ({ row }) => {
                        const name = getSupplierName(row.original);

                        return (
                            <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                                <Avatar
                                    sx={{
                                        width: 34,

                                        height: 34,

                                        background: "#0B1F3A",

                                        color: "#FFFFFF",

                                        fontSize: "11px",

                                        fontWeight: 800,

                                        boxShadow: "none",
                                    }}
                                >
                                    {name.slice(0, 2).toUpperCase()}
                                </Avatar>

                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    noWrap
                                    sx={{
                                        color: COLORS.text,

                                        maxWidth: 150,
                                    }}
                                >
                                    {name}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },

                {
                    Header: financeText("DATES"),

                    accessor: "invoiceDate",

                    width: "12%",

                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const returnDocument = isReturnInvoice(invoice);

                        const dueDate = getValue(invoice, "dueDate", "due_date");

                        const overdueDays = returnDocument
                            ? 0
                            : getOverdueDays(dueDate, getPaymentStatus(invoice));

                        return (
                            <MDBox>
                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        color: COLORS.subtext,
                                    }}
                                >
                                    {financeText(returnDocument ? "Return" : "Invoice")}:{" "}
                                    {formatDate(
                                        returnDocument
                                            ? getDateValue(invoice)
                                            : getValue(invoice, "invoiceDate", "invoice_date"),
                                    )}
                                </MDTypography>

                                {!returnDocument && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        fontWeight="bold"
                                        sx={{
                                            color: overdueDays > 0 ? COLORS.red : COLORS.text,
                                        }}
                                    >
                                        {financeText("Due")}: {formatDate(dueDate)}
                                    </MDTypography>
                                )}

                                {overdueDays > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${overdueDays} ${financeText("days overdue")}`}
                                        sx={{
                                            mt: 0.5,

                                            height: 21,

                                            color: COLORS.red,

                                            background: COLORS.redLight,

                                            fontSize: "10px",

                                            fontWeight: 700,
                                        }}
                                    />
                                )}
                            </MDBox>
                        );
                    },
                },

                {
                    Header: financeText("AMOUNT"),

                    accessor: "totalAmount",

                    align: "right",

                    width: "13%",

                    Cell: ({ row }) => {
                        const invoice = row.original;

                        const currency = getCurrency(invoice);
                        const summary = getReturnSummary(invoice);
                        const returnDocument = isReturnInvoice(invoice);
                        const netPayable = getNetInvoicePayable(invoice);

                        return (
                            <MDBox textAlign="right">
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{
                                        color: returnDocument ? COLORS.purple : COLORS.red,
                                    }}
                                >
                                    {financeText(returnDocument ? "Return" : "Gross")}{" "}
                                    {formatCompact(
                                        returnDocument
                                            ? getRawReturnAmount(invoice)
                                            : getTotalAmount(invoice),
                                        currency,
                                    )}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                        color: COLORS.subtext,
                                    }}
                                >
                                    {returnDocument
                                        ? `${financeText(getReturnStatus(invoice))} ${financeText("document")}`
                                        : `${financeText("VAT")} ${formatCompact(getVatAmount(invoice), currency)}`}
                                </MDTypography>

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            returnDocument && netPayable < 0
                                                ? COLORS.green
                                                : COLORS.amber,
                                    }}
                                >
                                    {financeText(returnDocument ? "Impact" : "Net")}{" "}
                                    {formatCompact(netPayable, currency)}
                                </MDTypography>

                                {!returnDocument && summary.issuedAmount > 0 && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        sx={{ color: COLORS.green, fontWeight: 700 }}
                                    >
                                        {financeText("Returned")} {formatCompact(summary.issuedAmount, currency)}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },

                {
                    Header: financeText("COST RELATION"),

                    accessor: "includedInMasterCost",

                    width: "11%",

                    Cell: ({ row }) => {
                        const invoice = row.original;

                        const included = isIncludedInMasterCost(invoice);
                        const returnDocument = isReturnInvoice(invoice);

                        return (
                            <MDBox>
                                <Chip
                                    size="small"
                                    label={financeText(
                                        returnDocument
                                            ? "Return Adjustment"
                                            : included
                                                ? "Included"
                                                : "Additional"
                                    )}
                                    sx={{
                                        color: returnDocument
                                            ? COLORS.green
                                            : included
                                                ? COLORS.blue
                                                : COLORS.purple,

                                        background: returnDocument
                                            ? COLORS.greenLight
                                            : included
                                                ? COLORS.blueLight
                                                : COLORS.purpleLight,

                                        fontWeight: 700,

                                        borderRadius: "8px",
                                    }}
                                />

                                <MDTypography
                                    variant="caption"
                                    display="block"
                                    mt={0.5}
                                    sx={{
                                        color: COLORS.subtext,
                                    }}
                                >
                                    {returnDocument
                                        ? formatCompact(
                                            getNetInvoicePayable(invoice),
                                            getCurrency(invoice),
                                        )
                                        : included
                                            ? financeText("Already in master cost")
                                            : formatCompact(
                                                getNetSupplierInvoiceCost(invoice),
                                                getCurrency(invoice),
                                            )}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },

                {
                    Header: financeText("APPROVAL"),

                    accessor: "approvalStatus",

                    width: "9%",

                    Cell: ({ row }) => (
                        <FinanceStatusChip status={getApprovalStatus(row.original)} />
                    ),
                },

                {
                    Header: financeText("RETURN STATUS"),
                    accessor: "returnStatus",
                    width: "11%",
                    Cell: ({ row }) => {
                        const summary = getReturnSummary(row.original);
                        const style =
                            RETURN_STATUS_STYLES[summary.status] ||
                            RETURN_STATUS_STYLES["No Return"];

                        return (
                            <MDBox minWidth={120}>
                                <Chip
                                    size="small"
                                    label={financeText(summary.status)}
                                    sx={{
                                        color: style.color,
                                        background: style.background,
                                        border: `1px solid ${style.border}`,
                                        fontWeight: 800,
                                    }}
                                />
                                {summary.count > 0 && !isReturnInvoice(row.original) && (
                                    <MDTypography
                                        variant="caption"
                                        display="block"
                                        mt={0.45}
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {summary.count}{" "}
                                        {financeText(
                                            summary.count === 1
                                                ? "return document"
                                                : "return documents"
                                        )}
                                    </MDTypography>
                                )}
                            </MDBox>
                        );
                    },
                },

                {
                    Header: financeText("PAYMENT"),

                    accessor: "paymentStatus",

                    width: "9%",

                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const storedStatus = getPaymentStatus(invoice);

                        // The stored status stays "Not Due" until someone updates
                        // it, so a past-due unpaid invoice read "Not Due" right
                        // next to its "N days overdue" chip. Surface the real state.
                        const pastDue =
                            !isReturnInvoice(invoice) &&
                            ["not due", "pending", "unpaid", ""].includes(
                                String(storedStatus || "").toLowerCase(),
                            ) &&
                            getOverdueDays(
                                getValue(invoice, "dueDate", "due_date"),
                                storedStatus,
                            ) > 0;

                        return (
                            <FinanceStatusChip
                                status={pastDue ? "Overdue" : storedStatus}
                            />
                        );
                    },
                },

                {
                    Header: financeText("DUPLICATE"),

                    accessor: "duplicateCheckStatus",

                    width: "9%",

                    Cell: ({ row }) => (
                        <FinanceStatusChip status={getDuplicateStatus(row.original)} />
                    ),
                },

                {
                    Header: financeText("FILE"),

                    accessor: "uploadedFileUrl",

                    align: "center",

                    width: "7%",

                    Cell: ({ row }) => {
                        const invoice = row.original;

                        const hasFile =
                            invoice?.uploadedFileUrl ||
                            invoice?.uploaded_file_url ||
                            invoice?.pdfUrl ||
                            invoice?.pdf_url;

                        return hasFile ? (
                            <Tooltip title={financeText("View supplier invoice")} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => openDocument(invoice)}
                                    sx={{
                                        color: COLORS.red,

                                        background: COLORS.redLight,

                                        border: "1px solid #FECDCA",

                                        "&:hover": {
                                            background: "#FEE4E2",
                                        },
                                    }}
                                >
                                    <PictureAsPdf
                                        sx={{
                                            fontSize: "17px !important",
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Chip
                                size="small"
                                label={financeText("No File")}
                                sx={{
                                    color: COLORS.subtext,

                                    background: "#F2F4F7",

                                    fontWeight: 700,
                                }}
                            />
                        );
                    },
                },

                {
                    Header: financeText("ACTIONS"),

                    accessor: "actions",

                    align: "right",

                    width: "10%",

                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const returnDocument = isReturnInvoice(invoice);
                        const hasLinkedReturns =
                            !returnDocument && getReturnSummary(invoice).count > 0;

                        const isApproved = getApprovalStatus(invoice)
                            .toLowerCase()
                            .includes("approved");

                        // Exact match: "unpaid" also contains "paid", which
                        // was disabling the "Mark as paid" button below for
                        // invoices that were actually unpaid.
                        const isPaid = getPaymentStatus(invoice)
                            .toLowerCase() === "paid";
                        const payableClosed = getNetInvoicePayable(invoice) <= 0;

                        return (
                            <MDBox display="flex" justifyContent="flex-end" gap={0.55}>
                                <Tooltip
                                    title={
                                        returnDocument ? financeText("View return invoice") : financeText("Finance detail")
                                    }
                                    arrow
                                >
                                    <IconButton
                                        size="small"
                                        onClick={() => openFinanceDetail(invoice)}
                                        sx={{
                                            color: COLORS.blue,

                                            background: COLORS.blueLight,

                                            border: "1px solid #B2DDFF",

                                            "&:hover": {
                                                background: "#D1E9FF",
                                            },
                                        }}
                                    >
                                        <Visibility
                                            sx={{
                                                fontSize: "17px !important",
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>

                                {returnDocument && (
                                    <Tooltip title={financeText("Open original invoice")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openOriginalInvoice(invoice)}
                                            sx={{
                                                color: COLORS.navy,
                                                background: "#EEF2F6",
                                                border: "1px solid #D0D5DD",
                                                "&:hover": { background: "#E4E7EC" },
                                            }}
                                        >
                                            <ReceiptLong sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {hasLinkedReturns && (
                                    <Tooltip title={financeText("Open latest return invoice")} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => openLatestReturnInvoice(invoice)}
                                            sx={{
                                                color: COLORS.purple,
                                                background: COLORS.purpleLight,
                                                border: "1px solid #D9D6FE",
                                                "&:hover": { background: "#E9E7FD" },
                                            }}
                                        >
                                            <ReceiptLong sx={{ fontSize: "17px !important" }} />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                {!returnDocument && (
                                    <Tooltip
                                        title={isApproved ? financeText("Already approved") : financeText("Approve invoice")}
                                        arrow
                                    >
                    <span>
                      <IconButton
                          size="small"
                          disabled={isApproved}
                          onClick={() => approveInvoice(invoice)}
                          sx={{
                              color: COLORS.green,

                              background: COLORS.greenLight,

                              border: "1px solid #ABEFC6",

                              "&:hover": {
                                  background: "#D1FADF",
                              },

                              "&.Mui-disabled": {
                                  background: "#F2F4F7",

                                  borderColor: "#E4E7EC",
                              },
                          }}
                      >
                        <CheckCircle
                            sx={{
                                fontSize: "17px !important",
                            }}
                        />
                      </IconButton>
                    </span>
                                    </Tooltip>
                                )}

                                {!returnDocument && (
                                    <Tooltip
                                        title={
                                            payableClosed
                                                ? financeText("No payable balance after returns")
                                                : isPaid
                                                    ? financeText("Already paid")
                                                    : financeText("Mark as paid")
                                        }
                                        arrow
                                    >
                    <span>
                      <IconButton
                          size="small"
                          disabled={isPaid || payableClosed}
                          onClick={() => markPaid(invoice)}
                          sx={{
                              color: COLORS.purple,

                              background: COLORS.purpleLight,

                              border: "1px solid #D9D6FE",

                              "&:hover": {
                                  background: "#E9E7FD",
                              },

                              "&.Mui-disabled": {
                                  background: "#F2F4F7",

                                  borderColor: "#E4E7EC",
                              },
                          }}
                      >
                        <Paid
                            sx={{
                                fontSize: "17px !important",
                            }}
                        />
                      </IconButton>
                    </span>
                                    </Tooltip>
                                )}
                            </MDBox>
                        );
                    },
                },
            ],

            rows: filteredInvoices,
        }),

        [filteredInvoices, financeLanguage],
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",

                    background: COLORS.page,

                    mx: {
                        xs: -2,
                        md: -3,
                    },

                    px: {
                        xs: 2,
                        md: 3,
                    },
                }}
            >
                <Card
                    sx={{
                        borderRadius: "22px",

                        color: COLORS.white,

                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",

                        boxShadow: "0 18px 46px rgba(11, 31, 58, 0.22)",

                        overflow: "hidden",

                        position: "relative",

                        mb: 3,

                        "&::after": {
                            content: '""',

                            position: "absolute",

                            width: 360,

                            height: 360,

                            borderRadius: "50%",

                            right: -130,

                            top: -180,

                            background: "rgba(255,255,255,.07)",
                        },

                        "&::before": {
                            content: '""',

                            position: "absolute",

                            width: 230,

                            height: 230,

                            borderRadius: "50%",

                            right: 120,

                            bottom: -170,

                            background: "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox
                        p={{
                            xs: 2.5,
                            md: 3.5,
                        }}
                        position="relative"
                        zIndex={1}
                    >
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} lg={7}>
                                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background: "rgba(255,255,255,.12)",
                                        }}
                                    >
                                        <WorkspacePremium
                                            sx={{
                                                color: "#FFFFFF",

                                                fontSize: 20,
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color: "rgba(255,255,255,.72)",

                                            letterSpacing: ".12em",
                                        }}
                                    >
                                        {financeText("CORPORATE SUPPLIER INVOICE CENTER")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color: "#FFFFFF",

                                        lineHeight: 1.12,
                                    }}
                                >
                                    {financeText("Supplier Invoices")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color: "rgba(255,255,255,.72)",

                                        maxWidth: 720,
                                    }}
                                >
                                    {financeText(
                                        "Manage supplier invoices, master cost relations, duplicate controls, approvals, payment statuses and maturity dates."
                                    )}
                                </MDTypography>

                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    flexWrap="wrap"
                                    gap={1}
                                    mt={2.5}
                                >
                                    <Chip
                                        icon={
                                            <CheckCircle
                                                sx={{
                                                    color: "#75E0A7 !important",
                                                }}
                                            />
                                        }
                                        label={`${filteredInvoices.length} ${financeText("supplier invoice records")}`}
                                        sx={{
                                            color: "#FFFFFF",

                                            background: "rgba(255,255,255,.10)",

                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth
                                                sx={{
                                                    color: "#B2DDFF !important",
                                                }}
                                            />
                                        }
                                        label={`${financeText("Updated")} ${lastUpdated.toLocaleTimeString([], {
                                            hour: "2-digit",

                                            minute: "2-digit",
                                        })}`}
                                        sx={{
                                            color: "#FFFFFF",

                                            background: "rgba(255,255,255,.10)",

                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background: "rgba(255,255,255,.09)",

                                        border: "1px solid rgba(255,255,255,.13)",

                                        backdropFilter: "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{
                                                color: "rgba(255,255,255,.72)",
                                            }}
                                        >
                                            {financeText("NET PAYABLE")}
                                        </MDTypography>

                                        <AccountBalanceWallet
                                            sx={{
                                                color: "#FEDF89",
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        sx={{
                                            color: "#FFFFFF",
                                        }}
                                    >
                                        {formatCompact(stats.netPayable, reportingCurrency)}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color: "rgba(255,255,255,.68)",
                                        }}
                                    >
                                        {financeText("Additional supplier cost")}{" "}
                                        {formatCompact(
                                            stats.additionalSupplierCost,
                                            reportingCurrency,
                                        )}
                                    </MDTypography>

                                    <MDBox display="flex" gap={1} mt={2} alignItems="stretch">
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "en" },
                                                    })
                                                }
                                                title="English"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "en"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "en"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "en"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/gb.svg"
                                                    alt="English"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    EN
                                                </MDTypography>
                                            </MDBox>

                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "tr" },
                                                    })
                                                }
                                                title="Türkçe"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "tr"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "tr"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "tr"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/tr.svg"
                                                    alt="Türkçe"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    TR
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={openUploadModal}
                                            sx={{
                                                background: "#FFFFFF !important",

                                                color: `${COLORS.navy} !important`,

                                                flex: 1,

                                                boxShadow: "none",
                                            }}
                                        >
                                            <UploadFile
                                                sx={{
                                                    mr: 0.5,
                                                }}
                                            />
                                            {financeText("Upload Invoice")}
                                        </MDButton>

                                        <Tooltip title={financeText("Refresh supplier invoices")}>
                                            <IconButton
                                                onClick={fetchSupplierInvoices}
                                                sx={{
                                                    color: "#FFFFFF",

                                                    border: "1px solid rgba(255,255,255,.18)",

                                                    borderRadius: "10px",
                                                }}
                                            >
                                                <Refresh />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                <Card sx={{ ...cardSx, mb: 2.5 }}>
                    <MDBox p={{ xs: 2, md: 2.5 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("Start Date")}
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label={financeText("End Date")}
                                    value={endDate}
                                    onChange={(event) => setEndDate(event.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Customer / Supplier")}
                                    value={
                                        selectedParty
                                            ? `${selectedParty.type}-${selectedParty.name}`
                                            : "all"
                                    }
                                    onChange={(event) => {
                                        if (event.target.value === "all") {
                                            setSelectedParty(null);
                                            return;
                                        }

                                        setSelectedParty(
                                            partyOptions.find(
                                                (option) =>
                                                    `${option.type}-${option.name}` ===
                                                    event.target.value,
                                            ) || null,
                                        );
                                    }}
                                >
                                    <MenuItem value="all">{financeText("All Customers / Suppliers")}</MenuItem>
                                    {partyOptions.map((option) => {
                                        const value = `${option.type}-${option.name}`;
                                        return (
                                            <MenuItem key={value} value={value}>
                                                {financeText(option.label)}
                                            </MenuItem>
                                        );
                                    })}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Invoice Origin")}
                                    value={invoiceOrigin}
                                    onChange={(event) => setInvoiceOrigin(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Origins")}</MenuItem>
                                    <MenuItem value="tms">TMS</MenuItem>
                                    <MenuItem value="manual">{financeText("Manual")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Document Type")}
                                    value={documentType}
                                    onChange={(event) => setDocumentType(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Documents")}</MenuItem>
                                    <MenuItem value="invoice">{financeText("Supplier Invoices")}</MenuItem>
                                    <MenuItem value="return_invoice">{financeText("Return Invoices")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Return Status")}
                                    value={returnStatus}
                                    onChange={(event) => setReturnStatus(event.target.value)}
                                >
                                    {returnStatusOptions.map((status) => (
                                        <MenuItem key={status} value={status}>
                                            {status === "all" ? financeText("All Return Statuses") : financeText(status)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Geographic Scope")}
                                    value={geographicScope}
                                    onChange={(event) => setGeographicScope(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Geographic Scopes")}</MenuItem>
                                    <MenuItem value="DOMESTIC">{financeText("Domestic")}</MenuItem>
                                    <MenuItem value="INTERNATIONAL">{financeText("International")}</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Transport Mode")}
                                    value={transportMode}
                                    onChange={(event) => setTransportMode(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Transport Modes")}</MenuItem>
                                    {transportModeOptions.map((mode) => (
                                        <MenuItem key={mode} value={mode}>
                                            {financeText(MODE_CONFIG[mode]?.label || mode)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Currency")}
                                    value={currencyFilter}
                                    onChange={(event) => setCurrencyFilter(event.target.value)}
                                >
                                    <MenuItem value="all">{financeText("All Currencies")}</MenuItem>
                                    {currencyOptions.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {/* Display currency -- purely a presentation
                                choice: it converts the aggregated totals
                                below into this currency. It does NOT filter
                                any records (that is the "Currency" select
                                above). */}
                            <Grid item xs={12} sm={6} lg={2}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={financeText("Display Currency")}
                                    value={selectedCurrency}
                                    onChange={(event) => setSelectedCurrency(event.target.value)}
                                    helperText={financeText("Totals are shown in this currency")}
                                >
                                    {CURRENCY_OPTIONS.map((currency) => (
                                        <MenuItem key={currency} value={currency}>
                                            {currency}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {loading ? (
                    <Grid container spacing={SPACE.rowGap}>
                        <Grid item xs={12} lg={7}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                        <Grid item xs={12} lg={5}>
                            <Skeleton variant="rounded" height={260} sx={{ borderRadius: SPACE.radiusCard }} />
                        </Grid>
                    </Grid>
                ) : (
                    <>
                        {/* Same hero + secondary-tiles pattern as the Finance
                            Dashboard, from the shared FinanceUiKit. */}

                        <Grid container spacing={SPACE.rowGap} mt={0.5}>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Invoices")}
                                    value={formatNumber(stats.count)}
                                    subtitle={`${stats.returnCount} ${financeText("return documents in filtered view")}`}
                                    icon={<UploadFile />}
                                    tone="navy"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Overdue")}
                                    value={formatNumber(stats.overdueCount)}
                                    subtitle={financeText("Past due supplier invoices")}
                                    icon={<WarningAmber />}
                                    tone="red"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} lg={4}>
                                <FinanceKpiTile
                                    title={financeText("Duplicate Flags")}
                                    value={formatNumber(stats.duplicateCount)}
                                    subtitle={financeText("Flagged as possible duplicates")}
                                    icon={<ContentCopy />}
                                    tone="purple"
                                />
                            </Grid>
                        </Grid>



                        <Card
                            sx={{
                                ...cardSx,
                                mt: 2.5,
                            }}
                        >
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    alignItems={{
                                        xs: "flex-start",
                                        md: "center",
                                    }}
                                    justifyContent="space-between"
                                    flexDirection={{
                                        xs: "column",
                                        md: "row",
                                    }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                        <MDBox
                                            width={38}
                                            height={38}
                                            borderRadius="11px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{
                                                color: COLORS.blue,

                                                background: COLORS.blueLight,

                                                border: "1px solid #B2DDFF",
                                            }}
                                        >
                                            <FilterAlt
                                                sx={{
                                                    fontSize: 19,
                                                }}
                                            />
                                        </MDBox>

                                        <MDBox>
                                            <MDTypography
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{
                                                    color: COLORS.text,
                                                }}
                                            >
                                                {financeText("Supplier Invoice Filters")}
                                            </MDTypography>

                                            <MDTypography
                                                variant="caption"
                                                sx={{
                                                    color: COLORS.subtext,
                                                }}
                                            >
                                                {financeText(
                                                    "Search supplier invoices by job, supplier, approval, payment and duplicate status."
                                                )}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>

                                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={fetchSupplierInvoices}
                                            sx={{
                                                borderRadius: "10px",
                                            }}
                                        >
                                            <Refresh
                                                sx={{
                                                    mr: 0.5,
                                                }}
                                            />
                                            {financeText("Refresh")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={resetFilters}
                                            sx={{
                                                borderRadius: "10px",
                                            }}
                                        >
                                            <RestartAlt
                                                sx={{
                                                    mr: 0.5,
                                                }}
                                            />
                                            {financeText("Reset")}
                                        </MDButton>

                                        <MDButton
                                            variant="contained"
                                            size="small"
                                            onClick={exportCsv}
                                            sx={{
                                                borderRadius: "10px",

                                                background: `${COLORS.navy} !important`,

                                                color: "#FFFFFF !important",

                                                boxShadow: "none",
                                            }}
                                        >
                                            <Download
                                                sx={{
                                                    mr: 0.5,
                                                }}
                                            />
                                            {financeText("Export CSV")}
                                        </MDButton>

                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            size="small"
                                            onClick={() =>
                                                navigate("/invoice/finance/supplier-costs")
                                            }
                                            sx={{
                                                borderRadius: "10px",
                                            }}
                                        >
                                            {financeText("Supplier Costs")}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} lg={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={financeText("Search")}
                                            placeholder={financeText("Shipment, supplier, invoice...")}
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search
                                                            fontSize="small"
                                                            sx={{
                                                                color: COLORS.subtext,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",

                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Approval")}
                                            value={approvalStatus}
                                            onChange={(event) =>
                                                setApprovalStatus(event.target.value)
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",

                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.approval.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Approvals") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Document Type")}
                                            value={documentType}
                                            onChange={(event) => setDocumentType(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            <MenuItem value="all">{financeText("All Documents")}</MenuItem>
                                            <MenuItem value="invoice">{financeText("Supplier Invoices")}</MenuItem>
                                            <MenuItem value="return_invoice">
                                                Return Invoices
                                            </MenuItem>
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Return Status")}
                                            value={returnStatus}
                                            onChange={(event) => setReturnStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {returnStatusOptions.map((status) => (
                                                <MenuItem key={status} value={status}>
                                                    {status === "all" ? financeText("All Return Statuses") : financeText(status)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Payment")}
                                            value={paymentStatus}
                                            onChange={(event) => setPaymentStatus(event.target.value)}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",

                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.payment.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Payments") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} sm={6} lg={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Duplicate")}
                                            value={duplicateStatus}
                                            onChange={(event) =>
                                                setDuplicateStatus(event.target.value)
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",

                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {statusOptions.duplicate.map((item) => (
                                                <MenuItem key={item} value={item}>
                                                    {item === "all" ? financeText("All Duplicate Statuses") : financeText(item)}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>

                                <MDBox
                                    mt={2.2}
                                    p={1.5}
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems={{
                                        xs: "flex-start",
                                        md: "center",
                                    }}
                                    justifyContent="space-between"
                                    flexDirection={{
                                        xs: "column",
                                        md: "row",
                                    }}
                                    gap={1}
                                    sx={{
                                        background: "#F9FAFB",

                                        border: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color: COLORS.subtext,
                                        }}
                                    >
                                        {financeText("Showing")}{" "}
                                        <strong
                                            style={{
                                                color: COLORS.text,
                                            }}
                                        >
                                            {filteredInvoices.length}
                                        </strong>{" "}
                                        {financeText("of")}{" "}
                                        <strong
                                            style={{
                                                color: COLORS.text,
                                            }}
                                        >
                                            {invoices.length}
                                        </strong>{" "}
                                        {financeText("supplier invoice documents")}
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        alignItems="center"
                                        flexWrap="wrap"
                                        gap={1}
                                    >
                                        <Chip
                                            size="small"
                                            label={`${financeText("Master Cost")} ${formatCompact(
                                                stats.masterTotalCost,
                                                reportingCurrency,
                                            )}`}
                                            sx={{
                                                color: COLORS.blue,

                                                background: COLORS.blueLight,

                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Additional")} ${formatCompact(
                                                stats.additionalSupplierCost,
                                                reportingCurrency,
                                            )}`}
                                            sx={{
                                                color: COLORS.purple,

                                                background: COLORS.purpleLight,

                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Returns")} ${formatCompact(
                                                stats.issuedReturnAmount,
                                                reportingCurrency,
                                            )}`}
                                            sx={{
                                                color: COLORS.green,
                                                background: COLORS.greenLight,
                                                fontWeight: 700,
                                            }}
                                        />

                                        <Chip
                                            size="small"
                                            label={`${financeText("Net")} ${formatCompact(
                                                stats.netPayable,
                                                reportingCurrency,
                                            )}`}
                                            sx={{
                                                color: COLORS.amber,

                                                background: COLORS.amberLight,

                                                fontWeight: 700,
                                            }}
                                        />
                                    </MDBox>
                                </MDBox>

                                <Divider
                                    sx={{
                                        my: 2.5,
                                    }}
                                />

                                <MDBox
                                    sx={{
                                        "& .MuiTableContainer-root": {
                                            border: `1px solid ${COLORS.border}`,

                                            borderRadius: "10px",

                                            overflowX: "auto",

                                            boxShadow: "none",
                                        },

                                        "& table": {
                                            minWidth: 1520,

                                            borderCollapse: "separate",

                                            borderSpacing: 0,
                                        },

                                        "& table thead": {
                                            background:
                                                "#F8FAFC",
                                        },

                                        "& table thead th": {
                                            background: "transparent",

                                            color: "#475467 !important",

                                            fontSize: "10px !important",

                                            fontWeight: "800 !important",

                                            letterSpacing: ".085em",

                                            paddingTop: "15px !important",

                                            paddingBottom: "15px !important",

                                            borderBottom: `1px solid ${COLORS.border} !important`,
                                        },

                                        "& table tbody td": {
                                            paddingTop: "14px !important",

                                            paddingBottom: "14px !important",

                                            borderBottom: "1px solid #F0F2F5 !important",

                                            verticalAlign: "middle",
                                        },

                                        "& table tbody tr": {
                                            background: "#FFFFFF",

                                            transition: "background .18s ease, box-shadow .18s ease",
                                        },

                                        "& table tbody tr:nth-of-type(even)": {
                                            background: "#FCFCFD",
                                        },

                                        "& table tbody tr:hover": {
                                            background: "#F5F8FF !important",

                                            boxShadow: "inset 3px 0 0 #175CD3",
                                        },

                                        "& table tbody tr:last-child td": {
                                            borderBottom: "0 !important",
                                        },

                                        "& .MuiTablePagination-root": {
                                            borderTop: `1px solid ${COLORS.border}`,

                                            background: "#FCFCFD",
                                        },
                                    }}
                                >
                                    <DataTable
                                        table={tableData}
                                        canSearch={false}
                                        entriesPerPage
                                        showTotalEntries
                                        isSorted
                                        noEndBorder
                                    />
                                </MDBox>
                            </MDBox>
                        </Card>

                        {/* İkincil grafik ve kırılımlar: varsayılan kapalı, üstteki filtrelere bağlı */}
                        <FinanceDetailsSection
                            storageKey="supplierInvoices"
                            title={financeText("Detailed analysis")}
                            subtitle={financeText("Charts and breakdowns for the current filters")}
                        >
                            <Grid container spacing={SPACE.rowGap}>
                            <Grid item xs={12} lg={7}>
                                <FinanceHeroStat
                                    label={financeText("Net Payable")}
                                    value={formatCompact(stats.netPayable, reportingCurrency)}
                                    tone="amber"
                                    icon={<Paid />}
                                    meta={financeText("Supplier exposure after issued returns")}
                                    context={[
                                        { label: financeText("Total Amount"), value: formatCompact(stats.totalAmount, reportingCurrency) },
                                        { label: financeText("Buying Rate"), value: formatCompact(stats.buyingRateTotal, reportingCurrency) },
                                        { label: financeText("Master Cost"), value: formatCompact(stats.masterTotalCost, reportingCurrency) },
                                    ]}
                                />
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <FinanceCard>
                                    <MDBox p={SPACE.cardPaddingLg} display="flex" flexDirection="column" height="100%">
                                        <FinanceSectionHeader
                                            title={getFinanceLanguage() === "tr" ? "Dikkat gerektiren" : "Needs attention"}
                                        />
                                        <MDBox display="flex" flexDirection="column" gap={1.25}>
                                            {[
                                                { label: financeText("Unpaid Payables"), value: formatCompact(stats.unpaidInvoiceTotal, reportingCurrency), sub: `${financeText("Paid")} ${formatCompact(stats.paidInvoiceTotal, reportingCurrency)}`, tone: "red" },
                                                { label: financeText("Approved"), value: formatNumber(stats.approvedCount), sub: `${stats.pendingCount} ${financeText("pending approvals")}`, tone: "green" },
                                                { label: financeText("Additional Cost"), value: formatCompact(stats.additionalSupplierCost, reportingCurrency), sub: `${stats.includedInMasterCount} ${financeText("invoices already included")}`, tone: "purple" },
                                            ].map((row) => (
                                                <MDBox
                                                    key={row.label}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    px={1.75}
                                                    py={1.25}
                                                    borderRadius={SPACE.radiusTile}
                                                    sx={{ background: COLORS.muted }}
                                                >
                                                    <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
                                                        <MDBox width={8} height={8} borderRadius="50%" flexShrink={0} sx={{ background: COLORS[row.tone] }} />
                                                        <MDBox minWidth={0}>
                                                            <MDTypography variant="button" fontWeight="medium" sx={{ color: COLORS.text, display: "block" }}>
                                                                {row.label}
                                                            </MDTypography>
                                                            <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                                                {row.sub}
                                                            </MDTypography>
                                                        </MDBox>
                                                    </MDBox>
                                                    <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS[row.tone], flexShrink: 0 }}>
                                                        {row.value}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    </MDBox>
                                </FinanceCard>
                            </Grid>
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            {Object.entries(transportSummary).map(([mode, data]) => (
                                <Grid item xs={12} md={4} key={mode}>
                                    <TransportInvoiceCard
                                        mode={mode}
                                        data={data}
                                        totalRecords={filteredInvoices.length}
                                        currency={reportingCurrency}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                            <Grid container spacing={2.5} mt={0.1}>
                            <Grid item xs={12} lg={7}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2.5}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Payables by Transport Mode")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText(
                                                        "Supplier invoice exposure across Ocean, Road and Air"
                                                    )}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox
                                                width={40}
                                                height={40}
                                                borderRadius="8px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.blue,
                                                    background: COLORS.blueLight,
                                                    border: "1px solid #B2DDFF",
                                                }}
                                            >
                                                <Analytics />
                                            </MDBox>
                                        </MDBox>
                                        <ModePayableChart
                                            summary={transportSummary}
                                            currency={reportingCurrency}
                                        />
                                    </MDBox>
                                </Card>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <Card sx={{ ...cardSx, height: "100%" }}>
                                    <MDBox p={3}>
                                        <MDBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            mb={2.2}
                                        >
                                            <MDBox>
                                                <MDTypography
                                                    variant="h6"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Shipment Type Mix")}
                                                </MDTypography>
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("Supplier invoice distribution by logistics service")}
                                                </MDTypography>
                                            </MDBox>
                                            <MDBox
                                                width={40}
                                                height={40}
                                                borderRadius="8px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                sx={{
                                                    color: COLORS.purple,
                                                    background: COLORS.purpleLight,
                                                    border: "1px solid #D9D6FE",
                                                }}
                                            >
                                                <Inventory2 />
                                            </MDBox>
                                        </MDBox>
                                        {shipmentTypeSummary.length ? (
                                            shipmentTypeSummary.map(([type, count], index) => {
                                                const max = shipmentTypeSummary[0]?.[1] || 1;
                                                return (
                                                    <MDBox key={type} mb={1.55}>
                                                        <MDBox
                                                            display="flex"
                                                            justifyContent="space-between"
                                                            mb={0.55}
                                                        >
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.text }}
                                                            >
                                                                {index + 1}. {type}
                                                            </MDTypography>
                                                            <MDTypography
                                                                variant="caption"
                                                                fontWeight="bold"
                                                                sx={{ color: COLORS.purple }}
                                                            >
                                                                {count}
                                                            </MDTypography>
                                                        </MDBox>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={(count / max) * 100}
                                                            sx={{
                                                                height: 7,
                                                                borderRadius: "999px",
                                                                backgroundColor: "#EEF2F6",
                                                                "& .MuiLinearProgress-bar": {
                                                                    borderRadius: "999px",
                                                                    background: COLORS.purple,
                                                                },
                                                            }}
                                                        />
                                                    </MDBox>
                                                );
                                            })
                                        ) : (
                                            <MDBox py={4} textAlign="center">
                                                <MDTypography
                                                    variant="caption"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {financeText("No shipment type data")}
                                                </MDTypography>
                                            </MDBox>
                                        )}
                                    </MDBox>
                                </Card>
                            </Grid>
                        </Grid>
                        </FinanceDetailsSection>

                    </>
                )}
            </MDBox>

            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: "10px",

                        overflow: "hidden",

                        border: `1px solid ${COLORS.border}`,

                        boxShadow: "0 24px 64px rgba(16,24,40,.18)",
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        px: 3,
                        py: 2.5,

                        background:
                            "#0B1F3A",

                        color: "#FFFFFF",
                    }}
                >
                    <MDBox display="flex" alignItems="center" gap={1.2}>
                        <MDBox
                            width={38}
                            height={38}
                            borderRadius="11px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                background: "rgba(255,255,255,.12)",
                            }}
                        >
                            <UploadFile />
                        </MDBox>

                        <MDBox>
                            <MDTypography
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color: "#FFFFFF",
                                }}
                            >
                                {financeText("Upload Supplier Invoice")}
                            </MDTypography>

                            <MDTypography
                                variant="caption"
                                sx={{
                                    color: "rgba(255,255,255,.68)",
                                }}
                            >
                                {financeText(
                                    "Register supplier invoice, finance job relation and master cost status."
                                )}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent
                    sx={{
                        p: 3,
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Finance Job ID")}
                                value={invoiceForm.financeJobId}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        financeJobId: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Shipment ID")}
                                value={invoiceForm.shipmentId}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        shipmentId: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Shipment / Job No")}
                                value={invoiceForm.jobNo}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        jobNo: event.target.value,

                                        shipmentNo: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Supplier Name")}
                                value={invoiceForm.supplierName}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        supplierName: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Supplier Tax No")}
                                value={invoiceForm.supplierTaxNo}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        supplierTaxNo: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("Invoice No")}
                                value={invoiceForm.invoiceNo}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        invoiceNo: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label={financeText("Invoice Date")}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={invoiceForm.invoiceDate}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        invoiceDate: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label={financeText("Due Date")}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                value={invoiceForm.dueDate}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        dueDate: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Total Amount")}
                                value={invoiceForm.totalAmount}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        totalAmount: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("VAT Amount")}
                                value={invoiceForm.vatAmount}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        vatAmount: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label={financeText("Net Payable")}
                                value={invoiceForm.netPayableAmount}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        netPayableAmount: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Currency")}
                                value={invoiceForm.currency}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        currency: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            >
                                <MenuItem value="EUR">EUR</MenuItem>

                                <MenuItem value="USD">USD</MenuItem>

                                <MenuItem value="TRY">TRY</MenuItem>

                                <MenuItem value="GBP">GBP</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Approval Status")}
                                value={invoiceForm.approvalStatus}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        approvalStatus: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            >
                                <MenuItem value="Pending">{financeText("Pending")}</MenuItem>

                                <MenuItem value="Approved">{financeText("Approved")}</MenuItem>

                                <MenuItem value="Rejected">{financeText("Rejected")}</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Payment Status")}
                                value={invoiceForm.paymentStatus}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        paymentStatus: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            >
                                <MenuItem value="Not Due">{financeText("Not Due")}</MenuItem>

                                <MenuItem value="Pending">{financeText("Pending")}</MenuItem>

                                <MenuItem value="Paid">{financeText("Paid")}</MenuItem>

                                <MenuItem value="Overdue">{financeText("Overdue")}</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label={financeText("Master Cost Relation")}
                                value={
                                    invoiceForm.includedInMasterCost ? "included" : "additional"
                                }
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        includedInMasterCost: event.target.value === "included",
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            >
                                <MenuItem value="additional">{financeText("Additional Supplier Cost")}</MenuItem>

                                <MenuItem value="included">
                                    {financeText("Already Included in Master Cost")}
                                </MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label={financeText("PDF / XML URL")}
                                value={invoiceForm.uploadedFileUrl}
                                onChange={(event) =>
                                    setInvoiceForm((previous) => ({
                                        ...previous,

                                        uploadedFileUrl: event.target.value,
                                    }))
                                }
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2.2,

                        background: "#F9FAFB",

                        borderTop: `1px solid ${COLORS.border}`,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={() => setModalOpen(false)}
                        sx={{
                            borderRadius: "10px",
                        }}
                    >
                        {financeText("Cancel")}
                    </MDButton>

                    <MDButton
                        variant="contained"
                        onClick={saveSupplierInvoice}
                        sx={{
                            borderRadius: "10px",

                            background: `${COLORS.navy} !important`,

                            color: "#FFFFFF !important",

                            boxShadow: "none",
                        }}
                    >
                        {financeText("Save Supplier Invoice")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default SupplierInvoices;